import React, { useEffect, useState, useMemo, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowLeft,
  Loader2,
  User,
  ClipboardList,
  Utensils,
  BarChart3,
  Droplets,
  Pencil,
  Trash2,
  Clock,
  Ticket,
  StickyNote,
  MessageSquare,
  Plus,
  X,
  BrainCircuit,
  MessageCircle,
  Download,
  FlaskConical,
  Ruler,
  Scale,
  Flame,
  RefreshCw,
} from "lucide-react";

import CompanyAdminLayout from "../../components/CompanyAdminLayout";
import CustomerMembershipBadge from "../../components/CustomerMembershipBadge";
import { useCompanyAdminAuth } from "../../contexts/CompanyAdminAuthContext";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Tabs } from "../../components/Tabs";
import {
  Table,
  TableHeader,
  TableBody,
  TableHead,
  TableRow,
  TableCell,
} from "../../components/ui/table";
import {
  companyAdminCustomersAPI,
  ticketsAPI,
} from "../../lib/companyAdminApi";
import DietPlanTab from "./DietPlanTab";
import CustomerHistoryTab from "./CustomerHistoryTab";
import LabReportsTab from "./LabReportsTab";

import { Calendar } from "../../components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "../../components/ui/popover";

import { Textarea } from "../../components/ui/textarea";
import { buildBrandingUrl } from "../../contexts/BrandingContext";

import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  ReferenceLine,
} from "recharts";
import { format, subDays } from "date-fns";
import { CalendarDays, Bell, CreditCard, IdCard } from "lucide-react";

const dietPlanTypeOptions = [
  { value: 1, label: "Strict Diet Plan" },
  { value: 2, label: "Moderate Diet Plan" },
  { value: 3, label: "Flexible Diet Plan" },
];

const goalOptions = [
  "Losing weight",
  "Maintaining weight",
  "Gaining weight",
  "Improve fitness",
  "Better nutrition",
  "Customized goals",
];

const lifestyleOptions = [
  "Lazy",
  "Lightly active",
  "Moderate active",
  "Very active",
  "Super active",
];

const genderOptions = ["Male", "Female", "Others"];

const heightUnitOptions = [
  { value: "ft", label: "FT" },
  { value: "cm", label: "CM" },
];

const weightUnitOptions = [
  { value: "kg", label: "KG" },
  { value: "lb", label: "LB" },
];

const normalizeCommaSeparated = (s) => {
  if (!s) return "";
  return String(s)
    .split(",")
    .map((x) => x.trim())
    .filter(Boolean)
    .join(", ");
};

const WaterIntakeTab = ({ customer, canEdit = true }) => {
  const [loading, setLoading] = useState(true);
  const [water, setWater] = useState(null);

  const [addOpen, setAddOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [editRecord, setEditRecord] = useState(null);

  const [quantity, setQuantity] = useState("");
  const [saving, setSaving] = useState(false);

  const fetchWater = async () => {
    setLoading(true);
    try {
      const res = await companyAdminCustomersAPI.waterGet({
        user_id: customer.user_id,
      });
      setWater(res.data.water || null);
    } catch (e) {
      setWater(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchWater();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customer.user_id]);

  const canAdd = !water;

  const handleAdd = async () => {
    setSaving(true);
    try {
      await companyAdminCustomersAPI.waterCreate({
        user_id: customer.user_id,
        quantity: quantity === "" ? null : Number(quantity),
        unit: "liter",
      });
      toast.success("Water intake added");
      setAddOpen(false);
      setQuantity("");
      fetchWater();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to add");
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = async () => {
    if (!editRecord?.id) return;
    setSaving(true);
    try {
      await companyAdminCustomersAPI.waterUpdate(editRecord.id, {
        quantity: quantity === "" ? null : Number(quantity),
        unit: "liter",
      });
      toast.success("Updated");
      setEditOpen(false);
      setEditRecord(null);
      setQuantity("");
      fetchWater();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to update");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!water?.id) return;
    // eslint-disable-next-line no-alert
    if (!window.confirm("Delete water intake record?")) return;
    try {
      await companyAdminCustomersAPI.waterDelete(water.id);
      toast.success("Deleted");
      fetchWater();
    } catch (e) {
      toast.error("Delete failed");
    }
  };

  const openEdit = () => {
    if (!water) return;
    setEditRecord(water);
    setQuantity(water.quantity ?? "");
    setEditOpen(true);
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <div className="text-sm font-semibold text-foreground">
            Water Intake
          </div>
          <div className="text-xs text-muted-foreground mt-1">
            Unit is always liter. Only one record per customer.
          </div>
        </div>
        {canEdit && (
          <Button onClick={() => setAddOpen(true)} disabled={!canAdd}>
            Add Water
          </Button>
        )}
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-5 h-5 animate-spin text-primary" />
        </div>
      ) : !water ? (
        <div className="text-sm text-muted-foreground">
          No water intake record yet.
        </div>
      ) : (
        <div className="rounded-md border border-border overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow className="bg-muted">
                <TableHead className="font-medium text-muted-foreground text-xs uppercase tracking-wider">
                  Unit
                </TableHead>
                <TableHead className="font-medium text-muted-foreground text-xs uppercase tracking-wider">
                  Quantity
                </TableHead>
                <TableHead className="font-medium text-muted-foreground text-xs uppercase tracking-wider text-right">
                  Action
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              <TableRow className="hover:bg-muted">
                <TableCell className="text-muted-foreground">
                  {water.unit || "liter"}
                </TableCell>
                <TableCell className="text-muted-foreground">
                  {water.quantity ?? "-"}
                </TableCell>
                <TableCell className="text-right">
                  <div className="flex justify-end gap-1">
                    {canEdit && (
                      <>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="hover:bg-muted"
                          onClick={openEdit}
                        >
                          <Pencil className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-destructive hover:text-destructive hover:bg-destructive/10"
                          onClick={handleDelete}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </>
                    )}
                  </div>
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </div>
      )}

      {addOpen && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => !saving && setAddOpen(false)} role="presentation">
          <div className="panel w-full max-w-md mx-4 p-0 app-modal-shell" onClick={(e) => e.stopPropagation()}>
            <div className="app-modal-header flex items-center justify-between px-6 py-4 border-b border-border">
              <h3 className="text-lg font-semibold text-foreground">
                Add Water Intake
              </h3>
              <button
                onClick={() => setAddOpen(false)}
                className="text-muted-foreground hover:text-foreground"
              >
                ✕
              </button>
            </div>
            <div className="app-modal-body p-6 space-y-4">
              <div>
                <Label className="text-foreground">Unit</Label>
                <Input value="liter" disabled />
              </div>
              <div>
                <Label className="text-foreground">Quantity</Label>
                <Input
                  type="number"
                  value={quantity}
                  onChange={(e) => setQuantity(e.target.value)}
                  placeholder="e.g., 2"
                />
              </div>
            </div>
            <div className="app-modal-footer flex items-center justify-end gap-3 px-6 py-4 border-t border-border">
              <Button
                variant="outline"
                onClick={() => setAddOpen(false)}
                disabled={saving}
              >
                Cancel
              </Button>
              <Button onClick={handleAdd} disabled={saving || !canAdd}>
                {saving ? "Saving..." : "Add"}
              </Button>
            </div>
          </div>
        </div>
      )}

      {editOpen && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => { if (!saving) { setEditOpen(false); setEditRecord(null); } }} role="presentation">
          <div className="panel w-full max-w-md mx-4 p-0 app-modal-shell" onClick={(e) => e.stopPropagation()}>
            <div className="app-modal-header flex items-center justify-between px-6 py-4 border-b border-border">
              <h3 className="text-lg font-semibold text-foreground">
                Edit Water Intake
              </h3>
              <button
                onClick={() => {
                  setEditOpen(false);
                  setEditRecord(null);
                }}
                className="text-muted-foreground hover:text-foreground"
              >
                ✕
              </button>
            </div>
            <div className="app-modal-body p-6 space-y-4">
              <div>
                <Label className="text-foreground">Unit</Label>
                <Input value="liter" disabled />
              </div>
              <div>
                <Label className="text-foreground">Quantity</Label>
                <Input
                  type="number"
                  value={quantity}
                  onChange={(e) => setQuantity(e.target.value)}
                  placeholder="e.g., 2"
                />
              </div>
            </div>
            <div className="app-modal-footer flex items-center justify-end gap-3 px-6 py-4 border-t border-border">
              <Button
                variant="outline"
                onClick={() => {
                  setEditOpen(false);
                  setEditRecord(null);
                }}
                disabled={saving}
              >
                Cancel
              </Button>
              <Button onClick={handleEdit} disabled={saving}>
                {saving ? "Saving..." : "Save"}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const WeightTrendChart = ({ userId }) => {
  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState([]);
  const [targetKg, setTargetKg] = useState(null);

  const [rangeOpen, setRangeOpen] = useState(false);
  const [range, setRange] = useState({ from: null, to: null });
  const [rangeLabel, setRangeLabel] = useState("");

  const fetchData = async (params) => {
    setLoading(true);
    try {
      const res = await companyAdminCustomersAPI.weightHistory(params);
      setItems(res.data.items || []);
      setTargetKg(
        res.data.target_weight_kg != null
          ? Number(res.data.target_weight_kg)
          : null,
      );
    } catch {
      setItems([]);
      setTargetKg(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // Default: last year of weigh-ins (fits Jan→Apr style trends)
    fetchData({ user_id: userId, days: 365 });
    setRangeLabel("");
    setRange({ from: null, to: null });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  const chartData = useMemo(() => {
    const points = (items || [])
      .map((it) => {
        const d = it.date ? new Date(it.date) : null;
        if (!d || Number.isNaN(d.getTime())) return null;
        const kg = Number(it.weight_kg);
        if (!Number.isFinite(kg)) return null;
        return {
          date: d,
          dateLabel: format(d, "MMM yyyy"),
          dateShort: format(d, "MMM d"),
          weight_kg: kg,
        };
      })
      .filter(Boolean);

    // One point per month (latest weigh-in) so the line reads like Jan 85 → Feb 82…
    const byMonth = new Map();
    for (const p of points) {
      const key = format(p.date, "yyyy-MM");
      const prev = byMonth.get(key);
      if (!prev || p.date >= prev.date) byMonth.set(key, p);
    }
    return [...byMonth.values()]
      .sort((a, b) => a.date - b.date)
      .map((p) => ({
        dateLabel: p.dateLabel,
        weight_kg: p.weight_kg,
        tooltipDate: p.dateShort,
      }));
  }, [items]);

  const yDomain = useMemo(() => {
    if (!chartData.length) return ["auto", "auto"];
    const vals = chartData.map((p) => p.weight_kg);
    if (targetKg != null && Number.isFinite(targetKg)) vals.push(targetKg);
    const min = Math.min(...vals);
    const max = Math.max(...vals);
    const pad = Math.max(2, (max - min) * 0.15 || 2);
    return [Math.floor(min - pad), Math.ceil(max + pad)];
  }, [chartData, targetKg]);

  const applyRange = () => {
    if (!range?.from || !range?.to) return;
    fetchData({
      user_id: userId,
      start_date: format(range.from, "yyyy-MM-dd"),
      end_date: format(range.to, "yyyy-MM-dd"),
    });
    setRangeLabel(
      `${format(range.from, "MMM d")} - ${format(range.to, "MMM d")}`,
    );
    setRangeOpen(false);
  };

  return (
    <div
      className="rounded-md border border-border bg-card p-4"
      data-testid="weight-trend-chart"
    >
      <div className="flex items-center justify-between gap-3">
        <div>
          <div className="text-sm font-semibold text-foreground">
            Weight trend
          </div>
          <div className="text-xs text-muted-foreground mt-1">
            {rangeLabel
              ? `Search results from ${rangeLabel}`
              : "Showing last 12 months (default)."}{" "}
            Weight in kg
            {targetKg != null && Number.isFinite(targetKg)
              ? ` · target ${targetKg} kg`
              : ""}
            .
          </div>
        </div>

        <div className="relative">
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              setRangeOpen((v) => {
                const next = !v;
                if (next && !range?.from && !range?.to) {
                  const to = new Date();
                  const from = subDays(to, 364);
                  setRange({ from, to });
                }
                return next;
              });
            }}
          >
            <CalendarDays className="w-4 h-4 mr-2" />
            Filter
          </Button>

          {rangeOpen && (
            <div className="absolute right-0 mt-2 w-[720px] max-w-[95vw] z-50 panel border border-border p-4 max-h-[75vh] overflow-y-auto">
              <div className="text-sm font-semibold text-foreground">
                Select date range
              </div>
              <div className="mt-3">
                <Calendar
                  mode="range"
                  numberOfMonths={2}
                  selected={range}
                  onSelect={(r) => {
                    if (!r || (!r.from && !r.to)) {
                      setRange({ from: null, to: null });
                      return;
                    }
                    setRange({ from: r.from || null, to: r.to || null });
                  }}
                  defaultMonth={range?.from || subDays(new Date(), 365)}
                  pagedNavigation
                />
              </div>
              <div className="flex justify-end gap-2 mt-3">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    setRangeOpen(false);
                    setRange({ from: null, to: null });
                  }}
                >
                  Cancel
                </Button>
                <Button
                  size="sm"
                  onClick={applyRange}
                  disabled={!range?.from || !range?.to}
                >
                  Apply
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="mt-4 h-[280px]">
        {loading ? (
          <div className="flex items-center justify-center h-full">
            <Loader2 className="w-5 h-5 animate-spin text-primary" />
          </div>
        ) : chartData.length === 0 ? (
          <div className="text-sm text-muted-foreground">
            No weight history yet. When the client logs weight in the app, it
            will appear here.
          </div>
        ) : (
          <ResponsiveContainer width="100%" height="100%">
            <LineChart
              data={chartData}
              margin={{ top: 10, right: 20, left: 0, bottom: 0 }}
            >
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="dateLabel" tick={{ fontSize: 12 }} />
              <YAxis
                tick={{ fontSize: 12 }}
                domain={yDomain}
                unit=" kg"
                width={56}
              />
              <Tooltip
                formatter={(value) => [`${value} kg`, "Weight"]}
                labelFormatter={(_, payload) => {
                  const tip = payload?.[0]?.payload?.tooltipDate;
                  return tip || "";
                }}
              />
              {targetKg != null && Number.isFinite(targetKg) && (
                <ReferenceLine
                  y={Number(targetKg)}
                  stroke="#8b5cf6"
                  strokeDasharray="4 4"
                  label={{
                    value: `Target ${targetKg} kg`,
                    position: "insideTopRight",
                    fill: "#8b5cf6",
                    fontSize: 12,
                  }}
                />
              )}
              <Line
                type="monotone"
                dataKey="weight_kg"
                stroke="#0ea5e9"
                strokeWidth={2}
                dot={{ r: 4 }}
                activeDot={{ r: 5 }}
              />
            </LineChart>
          </ResponsiveContainer>
        )}
      </div>

      {!loading && chartData.length > 0 && (
        <div className="mt-4 overflow-x-auto">
          <div className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">
            Weight history
          </div>
          <Table>
            <TableHeader>
              <TableRow className="bg-muted">
                <TableHead className="text-xs">Month</TableHead>
                <TableHead className="text-xs">Weight</TableHead>
                <TableHead className="text-xs">Target</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {chartData.map((row, i) => (
                <TableRow key={`${row.dateLabel}-${i}`}>
                  <TableCell className="text-muted-foreground">
                    {row.dateLabel}
                  </TableCell>
                  <TableCell className="text-muted-foreground">
                    {row.weight_kg} kg
                  </TableCell>
                  <TableCell className="text-muted-foreground">
                    {targetKg != null ? `${targetKg} kg` : "—"}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
};

const BODY_PART_SHORT_LABELS = {
  waist_belly: "Waist",
  hip: "Hip",
  chest: "Chest",
  left_upper_arm: "L Arm",
  right_upper_arm: "R Arm",
  left_thigh: "L Thigh",
  right_thigh: "R Thigh",
  left_calf: "L Calf",
  right_calf: "R Calf",
};

const DEFAULT_BODY_SITES = [
  { key: "waist_belly", label: "Waist (Belly) Circumference" },
  { key: "hip", label: "Hip Circumference" },
  { key: "chest", label: "Chest Circumference" },
  { key: "left_upper_arm", label: "Left Upper Arm" },
  { key: "right_upper_arm", label: "Right Upper Arm" },
  { key: "left_thigh", label: "Left Thigh (Leg)" },
  { key: "right_thigh", label: "Right Thigh (Leg)" },
  { key: "left_calf", label: "Left Calf" },
  { key: "right_calf", label: "Right Calf" },
];

/** Build per-site history from raw log events if API omits history_by_site. */
function buildHistoryBySiteFromItems(items, sites) {
  const keys = (sites?.length ? sites : DEFAULT_BODY_SITES).map((s) => s.key);
  const out = {};
  keys.forEach((k) => {
    out[k] = [];
  });
  for (const it of items || []) {
    const m = it?.measurements || {};
    const cmMap = it?.measurements_cm || {};
    for (const k of keys) {
      const raw = m[k];
      const n = raw == null || raw === "" ? null : Number(raw);
      if (n == null || !Number.isFinite(n) || n <= 0) continue;
      const cmRaw = cmMap[k];
      const cm =
        cmRaw != null && Number.isFinite(Number(cmRaw))
          ? Number(cmRaw)
          : Math.round(n * 2.54 * 10) / 10;
      out[k].push({
        id: it.id || it._id,
        date: it.date,
        value_in: n,
        value_cm: cm,
      });
    }
  }
  return out;
}

function buildLatestFromHistory(historyBySite, sites) {
  const measurements = {};
  const measurements_cm = {};
  const date_by_site = {};
  for (const s of sites || DEFAULT_BODY_SITES) {
    const series = historyBySite?.[s.key] || [];
    const last = series.length ? series[series.length - 1] : null;
    measurements[s.key] = last?.value_in ?? null;
    measurements_cm[s.key] = last?.value_cm ?? null;
    date_by_site[s.key] = last?.date ?? null;
  }
  return { measurements, measurements_cm, date_by_site };
}

const BodyMeasurementsPanel = ({ userId, canEdit = true }) => {
  const [loading, setLoading] = useState(true);
  const [sites, setSites] = useState(DEFAULT_BODY_SITES);
  const [historyBySite, setHistoryBySite] = useState({});
  const [current, setCurrent] = useState(null);
  const [targets, setTargets] = useState({});
  const [targetsCm, setTargetsCm] = useState({});
  const [siteKey, setSiteKey] = useState("waist_belly");
  const [days, setDays] = useState("all");
  const [targetDraft, setTargetDraft] = useState({});
  const [savingTargets, setSavingTargets] = useState(false);

  const fetchData = async () => {
    setLoading(true);
    try {
      const res = await companyAdminCustomersAPI.bodyMeasurementsList({
        user_id: userId,
        days: days === "all" ? "all" : Number(days),
      });
      const siteList =
        res.data?.sites?.length > 0 ? res.data.sites : DEFAULT_BODY_SITES;
      const items = res.data?.items || [];
      let bySite = res.data?.history_by_site || {};
      const hasAnySeries = Object.values(bySite).some(
        (arr) => Array.isArray(arr) && arr.length > 0,
      );
      if (!hasAnySeries && items.length) {
        bySite = buildHistoryBySiteFromItems(items, siteList);
      }

      let currentVal = res.data?.current || null;
      const hasCurrentValue = siteList.some(
        (s) => currentVal?.measurements?.[s.key] != null,
      );
      if (!hasCurrentValue) {
        currentVal = buildLatestFromHistory(bySite, siteList);
      }

      setSites(siteList);
      setHistoryBySite(bySite);
      setCurrent(currentVal);
      setTargets(res.data?.targets || {});
      setTargetsCm(res.data?.targets_cm || {});
      setTargetDraft(res.data?.targets || {});

      setSiteKey((prev) => {
        const stillValid = siteList.some((s) => s.key === prev);
        if (stillValid) return prev;
        const withData = siteList.find(
          (s) => (bySite?.[s.key] || []).length > 0,
        );
        return withData?.key || siteList[0]?.key || "waist_belly";
      });
    } catch {
      setSites(DEFAULT_BODY_SITES);
      setHistoryBySite({});
      setCurrent(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, days]);

  const siteList = sites.length ? sites : DEFAULT_BODY_SITES;
  const activeSite =
    siteList.find((s) => s.key === siteKey) || siteList[0] || null;
  const activeKey = activeSite?.key || "waist_belly";
  const shortLabel =
    BODY_PART_SHORT_LABELS[activeKey] || activeSite?.label || activeKey;

  const chartData = useMemo(() => {
    const list = historyBySite?.[activeKey] || [];
    return (list || [])
      .map((p) => {
        const d = p.date ? new Date(p.date) : null;
        if (!d || Number.isNaN(d.getTime())) return null;
        const inches =
          p.value_in != null && Number.isFinite(Number(p.value_in))
            ? Number(p.value_in)
            : null;
        if (inches == null || inches <= 0) return null;
        const cm =
          p.value_cm != null && Number.isFinite(Number(p.value_cm))
            ? Number(p.value_cm)
            : Math.round(inches * 2.54 * 10) / 10;
        return {
          date: d,
          dateLabel: format(d, "dd MMM"),
          dateFull: format(d, "dd MMM yyyy · HH:mm"),
          value_in: inches,
          value_cm: cm,
        };
      })
      .filter(Boolean);
  }, [historyBySite, activeKey]);

  const historyRows = useMemo(() => {
    const list = historyBySite?.[activeKey] || [];
    return [...(list || [])].reverse().map((p, idx) => {
      const d = p.date ? new Date(p.date) : null;
      return {
        key: `${p.id || idx}-${p.date}`,
        date:
          d && !Number.isNaN(d.getTime())
            ? format(d, "dd MMM yyyy · HH:mm")
            : "—",
        inches: p.value_in != null ? Number(p.value_in) : null,
        cm: p.value_cm != null ? Number(p.value_cm) : null,
      };
    });
  }, [historyBySite, activeKey]);

  const currentIn = current?.measurements?.[activeKey];
  const currentCm = current?.measurements_cm?.[activeKey];
  const loggedOn = current?.date_by_site?.[activeKey]
    ? format(new Date(current.date_by_site[activeKey]), "dd MMM yyyy")
    : null;
  const targetIn =
    targetDraft?.[activeKey] !== "" && targetDraft?.[activeKey] != null
      ? Number(targetDraft[activeKey])
      : targets?.[activeKey] != null
        ? Number(targets[activeKey])
        : null;
  const targetCmLine =
    targetsCm?.[activeKey] != null
      ? Number(targetsCm[activeKey])
      : targetIn != null && Number.isFinite(targetIn)
        ? Math.round(targetIn * 2.54 * 10) / 10
        : null;

  const activeLogCount = (historyBySite?.[activeKey] || []).length;
  const canSaveTarget =
    canEdit && activeLogCount > 0 && !savingTargets;

  const handleSaveTargets = async () => {
    if (activeLogCount === 0) {
      toast.error("No measurement logs for this body part yet");
      return;
    }
    setSavingTargets(true);
    try {
      await companyAdminCustomersAPI.bodyMeasurementsTargets({
        user_id: userId,
        targets: targetDraft,
      });
      toast.success("Target saved");
      await fetchData();
    } catch (e) {
      toast.error(e.response?.data?.message || "Failed to save target");
    } finally {
      setSavingTargets(false);
    }
  };

  return (
    <div className="space-y-4" data-testid="body-measurements-panel">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="text-base font-semibold text-foreground">
            Body measurements
          </div>
          <div className="text-xs text-muted-foreground mt-1">
            Client logs in inches. Pick a body part tab to see its chart and
            history.
          </div>
        </div>
        <select
          className="form-select h-9 w-auto min-w-[130px]"
          value={days}
          onChange={(e) => setDays(e.target.value)}
          data-testid="body-meas-range-select"
        >
          <option value="30">Last 30 days</option>
          <option value="90">Last 90 days</option>
          <option value="365">Last year</option>
          <option value="all">All history</option>
        </select>
      </div>

      {/* Body part tabs */}
      <div
        className="flex gap-1 overflow-x-auto border-b border-border pb-0"
        data-testid="body-meas-site-tabs"
      >
        {siteList.map((s) => {
          const count = (historyBySite?.[s.key] || []).length;
          const selected = s.key === activeKey;
          const short = BODY_PART_SHORT_LABELS[s.key] || s.label;
          return (
            <button
              key={s.key}
              type="button"
              onClick={() => setSiteKey(s.key)}
              className={
                "shrink-0 px-3 py-2.5 text-sm font-semibold border-b-2 -mb-px transition-colors " +
                (selected
                  ? "border-primary text-primary"
                  : "border-transparent text-muted-foreground hover:text-foreground")
              }
              data-testid={`body-meas-tab-${s.key}`}
            >
              {short}
              {count > 0 ? (
                <span className="ml-1.5 text-xs font-medium text-muted-foreground">
                  ({count})
                </span>
              ) : null}
            </button>
          );
        })}
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-16">
          <Loader2 className="w-5 h-5 animate-spin text-primary" />
        </div>
      ) : (
        <>
          {/* Selected part summary */}
          <div className="rounded-md border border-border bg-card p-4">
            <div className="text-sm font-semibold text-foreground mb-3">
              {activeSite?.label || shortLabel}
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div className="rounded-md bg-muted/40 px-3 py-3">
                <div className="text-[11px] uppercase tracking-wider text-muted-foreground font-semibold">
                  Current
                </div>
                <div className="text-lg font-semibold text-foreground mt-1">
                  {currentIn != null ? `${currentIn} in` : "—"}
                </div>
                <div className="text-xs text-muted-foreground mt-0.5">
                  {currentCm != null
                    ? `${currentCm} cm`
                    : "No value logged yet"}
                  {loggedOn ? ` · ${loggedOn}` : ""}
                </div>
              </div>
              <div className="rounded-md bg-muted/40 px-3 py-3">
                <div className="text-[11px] uppercase tracking-wider text-muted-foreground font-semibold">
                  Target
                </div>
                {canEdit ? (
                  <div className="flex items-center gap-2 mt-1">
                    <Input
                      type="number"
                      step="0.1"
                      min="0"
                      className="h-9 w-24"
                      disabled={activeLogCount === 0 || savingTargets}
                      value={
                        targetDraft?.[activeKey] === "" ||
                        targetDraft?.[activeKey] == null
                          ? ""
                          : targetDraft[activeKey]
                      }
                      onChange={(e) =>
                        setTargetDraft((prev) => ({
                          ...prev,
                          [activeKey]:
                            e.target.value === ""
                              ? ""
                              : Number(e.target.value),
                        }))
                      }
                      placeholder="in"
                    />
                    <span className="text-sm text-muted-foreground">in</span>
                    <Button
                      size="sm"
                      onClick={handleSaveTargets}
                      disabled={!canSaveTarget}
                      title={
                        activeLogCount === 0
                          ? "Add a measurement log before setting a target"
                          : undefined
                      }
                    >
                      {savingTargets ? "…" : "Save"}
                    </Button>
                  </div>
                ) : (
                  <div className="text-lg font-semibold text-foreground mt-1">
                    {targetIn != null && Number.isFinite(targetIn)
                      ? `${targetIn} in`
                      : "—"}
                  </div>
                )}
                {targetCmLine != null && Number.isFinite(targetCmLine) ? (
                  <div className="text-xs text-muted-foreground mt-1">
                    {targetCmLine} cm
                  </div>
                ) : null}
              </div>
              <div className="rounded-md bg-muted/40 px-3 py-3">
                <div className="text-[11px] uppercase tracking-wider text-muted-foreground font-semibold">
                  Logs
                </div>
                <div className="text-lg font-semibold text-foreground mt-1">
                  {(historyBySite?.[activeKey] || []).length}
                </div>
                <div className="text-xs text-muted-foreground mt-0.5">
                  {(historyBySite?.[activeKey] || []).length === 1
                    ? "record"
                    : "records"}{" "}
                  in this range
                </div>
              </div>
            </div>
          </div>

          {/* Chart — inches (how client logs) */}
          <div className="rounded-md border border-border bg-card p-4 space-y-2">
            <div className="text-sm font-semibold text-foreground">
              Progress — {shortLabel}
            </div>
            <div className="text-xs text-muted-foreground">
              Each point is one log. Hover to see date and size.
            </div>
            <div className="h-[280px]">
              {chartData.length === 0 ? (
                <div className="flex items-center justify-center h-full text-sm text-muted-foreground">
                  No logs for {shortLabel} in this date range.
                </div>
              ) : (
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart
                    data={chartData}
                    margin={{ top: 12, right: 16, left: 0, bottom: 4 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="dateLabel" tick={{ fontSize: 11 }} />
                    <YAxis
                      tick={{ fontSize: 12 }}
                      unit=" in"
                      width={52}
                      domain={["auto", "auto"]}
                    />
                    <Tooltip
                      content={({ active, payload }) => {
                        if (!active || !payload?.length) return null;
                        const row = payload[0]?.payload || {};
                        return (
                          <div className="rounded-md border border-border bg-card px-3 py-2 shadow-md text-sm">
                            <div className="font-semibold text-foreground">
                              {shortLabel}
                            </div>
                            <div className="text-xs text-muted-foreground mt-0.5">
                              {row.dateFull || "—"}
                            </div>
                            <div className="mt-1.5 font-medium text-foreground">
                              {row.value_in != null
                                ? `${row.value_in} inches`
                                : "—"}
                              {row.value_cm != null ? (
                                <span className="text-muted-foreground font-normal">
                                  {" "}
                                  ({row.value_cm} cm)
                                </span>
                              ) : null}
                            </div>
                          </div>
                        );
                      }}
                    />
                    {targetIn != null &&
                      Number.isFinite(targetIn) &&
                      targetIn > 0 && (
                        <ReferenceLine
                          y={targetIn}
                          stroke="#8b5cf6"
                          strokeDasharray="4 4"
                          label={{
                            value: `Target ${targetIn} in`,
                            position: "insideTopRight",
                            fill: "#8b5cf6",
                            fontSize: 11,
                          }}
                        />
                      )}
                    <Line
                      type="monotone"
                      dataKey="value_in"
                      name="Measurement"
                      stroke="#f97316"
                      strokeWidth={2}
                      dot={{ r: 4 }}
                      activeDot={{ r: 6 }}
                      connectNulls
                    />
                  </LineChart>
                </ResponsiveContainer>
              )}
            </div>
          </div>

          {/* History for selected tab */}
          <div className="rounded-md border border-border bg-card p-4 space-y-3">
            <div className="flex items-center justify-between gap-2">
              <div className="text-sm font-semibold text-foreground">
                History — {shortLabel}
              </div>
              <div className="text-xs text-muted-foreground">
                Newest first · {historyRows.length}{" "}
                {historyRows.length === 1 ? "log" : "logs"}
              </div>
            </div>
            {historyRows.length === 0 ? (
              <div className="text-sm text-muted-foreground py-4">
                No measurement logs for this body part yet.
              </div>
            ) : (
              <div className="overflow-x-auto max-h-[360px] overflow-y-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-muted">
                      <TableHead className="text-xs">When</TableHead>
                      <TableHead className="text-xs">Inches</TableHead>
                      <TableHead className="text-xs">Centimeters</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {historyRows.map((r) => (
                      <TableRow key={r.key}>
                        <TableCell className="text-muted-foreground">
                          {r.date}
                        </TableCell>
                        <TableCell className="font-medium text-foreground">
                          {r.inches != null ? `${r.inches} in` : "—"}
                        </TableCell>
                        <TableCell className="text-muted-foreground">
                          {r.cm != null ? `${r.cm} cm` : "—"}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};

const WaterIntakeChart = ({ userId }) => {
  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState([]);
  const [goal, setGoal] = useState(null);

  const [rangeOpen, setRangeOpen] = useState(false);
  const [range, setRange] = useState({ from: null, to: null });
  const [rangeLabel, setRangeLabel] = useState("");

  const fetchData = async (params) => {
    setLoading(true);
    try {
      const [histRes, goalRes] = await Promise.all([
        companyAdminCustomersAPI.waterIntakeHistory(params),
        companyAdminCustomersAPI.waterGet({ user_id: userId }),
      ]);

      setItems(histRes.data.items || []);
      setGoal(goalRes.data.water?.quantity ?? null);
    } catch (e) {
      setItems([]);
      setGoal(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData({ user_id: userId, days: 7 });
    setRangeLabel("");
    setRange({ from: null, to: null });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  const chartData = useMemo(() => {
    return (items || []).map((it) => {
      const d = it.date ? new Date(it.date) : null;
      return {
        dateLabel: d ? format(d, "MMM d") : it.date,
        portion: Number(it.portion ?? 0),
      };
    });
  }, [items]);

  const applyRange = () => {
    if (!range?.from || !range?.to) return;

    fetchData({
      user_id: userId,
      start_date: format(range.from, "yyyy-MM-dd"),
      end_date: format(range.to, "yyyy-MM-dd"),
    });

    setRangeLabel(
      `${format(range.from, "MMM d")} - ${format(range.to, "MMM d")}`,
    );
    setRangeOpen(false);
  };

  return (
    <div className="rounded-md border border-border bg-card p-4">
      <div className="flex items-center justify-between gap-3">
        <div>
          <div className="text-sm font-semibold text-foreground">
            Water Intake
          </div>
          <div className="text-xs text-muted-foreground mt-1">
            {rangeLabel
              ? `Search results from ${rangeLabel}`
              : "Showing last 7 days (default)."}{" "}
            Portion in liters.
          </div>
        </div>

        <div className="relative">
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              setRangeOpen((v) => {
                const next = !v;
                if (next && !range?.from && !range?.to) {
                  const to = new Date();
                  const from = subDays(to, 6);
                  setRange({ from, to });
                }
                return next;
              });
            }}
          >
            <CalendarDays className="w-4 h-4 mr-2" />
            Filter
          </Button>

          {rangeOpen && (
            <div className="absolute right-0 mt-2 w-[720px] max-w-[95vw] z-50 panel border border-border p-4 max-h-[75vh] overflow-y-auto">
              <div className="text-sm font-semibold text-foreground">
                Select date range
              </div>
              <div className="mt-3">
                <Calendar
                  mode="range"
                  numberOfMonths={2}
                  selected={range}
                  onSelect={(r) => {
                    // react-day-picker range: { from?: Date; to?: Date }
                    if (!r || (!r.from && !r.to)) {
                      setRange({ from: null, to: null });
                      return;
                    }
                    setRange({ from: r.from || null, to: r.to || null });
                  }}
                  defaultMonth={range?.from || subDays(new Date(), 7)}
                  pagedNavigation
                />
              </div>
              <div className="flex justify-end gap-2 mt-3">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    setRangeOpen(false);
                    setRange({ from: null, to: null });
                  }}
                >
                  Cancel
                </Button>
                <Button
                  size="sm"
                  onClick={applyRange}
                  disabled={!range?.from || !range?.to}
                >
                  Apply
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="mt-4 h-[280px]">
        {loading ? (
          <div className="flex items-center justify-center h-full">
            <Loader2 className="w-5 h-5 animate-spin text-primary" />
          </div>
        ) : chartData.length === 0 ? (
          <div className="text-sm text-muted-foreground">
            No water intake data found for selected period.
          </div>
        ) : (
          <ResponsiveContainer width="100%" height="100%">
            <LineChart
              data={chartData}
              margin={{ top: 10, right: 20, left: 0, bottom: 0 }}
            >
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="dateLabel" tick={{ fontSize: 12 }} />
              <YAxis tick={{ fontSize: 12 }} />
              <Tooltip />
              {goal !== null && (
                <ReferenceLine
                  y={Number(goal)}
                  stroke="#8b5cf6"
                  strokeDasharray="4 4"
                  label={{
                    value: `Goal ${goal}L`,
                    position: "insideTopRight",
                    fill: "#8b5cf6",
                    fontSize: 12,
                  }}
                />
              )}
              <Line
                type="monotone"
                dataKey="portion"
                stroke="#22c55e"
                strokeWidth={2}
                dot={{ r: 3 }}
              />
            </LineChart>
          </ResponsiveContainer>
        )}
      </div>
    </div>
  );
};

const DietIntakeCharts = ({ userId, planType }) => {
  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState([]);

  const [rangeOpen, setRangeOpen] = useState(false);
  const [range, setRange] = useState({ from: null, to: null });
  const [rangeLabel, setRangeLabel] = useState("");

  const fetchData = async (params) => {
    setLoading(true);
    try {
      const res = await companyAdminCustomersAPI.dietIntakeHistory(params);
      setItems(res.data.items || []);
    } catch (e) {
      setItems([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData({ user_id: userId, type: planType, days: 7 });
    setRangeLabel("");
    setRange({ from: null, to: null });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, planType]);

  const chartData = useMemo(() => {
    return (items || []).map((it) => {
      const d = it.date ? new Date(it.date) : null;
      return {
        dateLabel: d ? format(d, "MMM d") : it.date,
        calories: Number(it.calories ?? 0),
        fat: Number(it.fat ?? 0),
        carbs: Number(it.carbs ?? 0),
        protein: Number(it.protein ?? 0),
        target: Number(it.target ?? 1),
      };
    });
  }, [items]);

  const applyRange = () => {
    if (!range?.from || !range?.to) return;

    fetchData({
      user_id: userId,
      type: planType,
      start_date: format(range.from, "yyyy-MM-dd"),
      end_date: format(range.to, "yyyy-MM-dd"),
    });

    setRangeLabel(
      `${format(range.from, "MMM d")} - ${format(range.to, "MMM d")}`,
    );
    setRangeOpen(false);
  };

  const getDot = (colorMap) => (props) => {
    const { cx, cy, payload } = props;
    if (!payload) return null;
    const t = Number(payload.target ?? 1);
    const c = colorMap[t] || colorMap[1];
    return (
      <circle cx={cx} cy={cy} r={4} fill={c} stroke="white" strokeWidth={1} />
    );
  };

  const targetLegend = (
    <div className="flex items-center gap-4 text-xs text-muted-foreground">
      <div className="flex items-center gap-2">
        <span className="inline-block w-2.5 h-2.5 rounded-full bg-green-500" />{" "}
        Target=Equal
      </div>
      <div className="flex items-center gap-2">
        <span className="inline-block w-2.5 h-2.5 rounded-full bg-yellow-500" />{" "}
        Target=Less
      </div>
      <div className="flex items-center gap-2">
        <span className="inline-block w-2.5 h-2.5 rounded-full bg-red-500" />{" "}
        Target=More
      </div>
    </div>
  );

  const ChartCard = ({ title, dataKey, stroke }) => (
    <div className="rounded-md border border-border bg-card p-4">
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="text-sm font-semibold text-foreground">{title}</div>
          <div className="text-xs text-muted-foreground mt-1">
            {rangeLabel
              ? `Search results from ${rangeLabel}`
              : "Showing last 7 days (default)."}
          </div>
        </div>
        {targetLegend}
      </div>

      <div className="mt-4 h-[240px]">
        {loading ? (
          <div className="flex items-center justify-center h-full">
            <Loader2 className="w-5 h-5 animate-spin text-primary" />
          </div>
        ) : chartData.length === 0 ? (
          <div className="text-sm text-muted-foreground">
            No diet intake data found for selected period.
          </div>
        ) : (
          <ResponsiveContainer width="100%" height="100%">
            <LineChart
              data={chartData}
              margin={{ top: 10, right: 20, left: 0, bottom: 0 }}
            >
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="dateLabel" tick={{ fontSize: 12 }} />
              <YAxis tick={{ fontSize: 12 }} />
              <Tooltip />
              <Line
                type="monotone"
                dataKey={dataKey}
                stroke={stroke}
                strokeWidth={2}
                dot={getDot({ 1: "#22c55e", 2: "#eab308", 3: "#ef4444" })}
              />
            </LineChart>
          </ResponsiveContainer>
        )}
      </div>
    </div>
  );

  return (
    <div className="rounded-md border border-border bg-card p-4">
      <div className="flex items-center justify-between gap-3">
        <div>
          <div className="text-sm font-semibold text-foreground">
            Diet Intake History
          </div>
          <div className="text-xs text-muted-foreground mt-1">
            Plan Type: {planType}
          </div>
        </div>
        <div className="relative">
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              setRangeOpen((v) => {
                const next = !v;
                if (next && !range?.from && !range?.to) {
                  const to = new Date();
                  const from = subDays(to, 6);
                  setRange({ from, to });
                }
                return next;
              });
            }}
          >
            <CalendarDays className="w-4 h-4 mr-2" />
            Filter
          </Button>
          {rangeOpen && (
            <div className="absolute right-0 mt-2 w-[720px] max-w-[95vw] z-50 panel border border-border p-4 max-h-[75vh] overflow-y-auto">
              <div className="text-sm font-semibold text-foreground">
                Select date range
              </div>
              <div className="mt-3">
                <Calendar
                  mode="range"
                  numberOfMonths={2}
                  selected={range}
                  onSelect={(r) => {
                    if (!r || (!r.from && !r.to)) {
                      setRange({ from: null, to: null });
                      return;
                    }
                    setRange({ from: r.from || null, to: r.to || null });
                  }}
                  defaultMonth={range?.from || subDays(new Date(), 7)}
                  pagedNavigation
                />
              </div>
              <div className="flex justify-end gap-2 mt-3">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    setRangeOpen(false);
                    setRange({ from: null, to: null });
                  }}
                >
                  Cancel
                </Button>
                <Button
                  size="sm"
                  onClick={applyRange}
                  disabled={!range?.from || !range?.to}
                >
                  Apply
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mt-4">
        <ChartCard title="Calories" dataKey="calories" stroke="#8b5cf6" />
        <ChartCard title="Fat" dataKey="fat" stroke="#f97316" />
        <ChartCard title="Carbs" dataKey="carbs" stroke="#0ea5e9" />
        <ChartCard title="Proteins" dataKey="protein" stroke="#22c55e" />
      </div>
    </div>
  );
};

/** Stats tab: one topic at a time (Weight / Water / Nutrition). */
const StatsPanel = ({ userId, planType }) => {
  const [statsTab, setStatsTab] = useState("weight");

  const tabs = [
    {
      id: "weight",
      label: "Weight",
      hint: "Body weight trend vs target",
      icon: Scale,
    },
    {
      id: "water",
      label: "Water",
      hint: "Daily water intake vs goal",
      icon: Droplets,
    },
    {
      id: "nutrition",
      label: "Nutrition",
      hint: "Calories, fat, carbs & protein from logged food",
      icon: Flame,
    },
  ];

  const active = tabs.find((t) => t.id === statsTab) || tabs[0];

  const exportPdf = async () => {
    try {
      const res = await companyAdminCustomersAPI.progressReportPdf({
        user_id: userId,
      });
      const blob = new Blob([res.data], { type: "application/pdf" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "progress-report.pdf";
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Progress report PDF downloaded");
    } catch (e) {
      toast.error(
        e.response?.data?.message || "Failed to export progress PDF",
      );
    }
  };

  return (
    <div className="space-y-4" data-testid="stats-panel">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="text-base font-semibold text-foreground">Stats</div>
          <div className="text-xs text-muted-foreground mt-1">
            Choose a tab below to view one chart at a time.
          </div>
        </div>
        <Button
          variant="outline"
          className="gap-2"
          data-testid="export-progress-pdf"
          onClick={exportPdf}
        >
          <Download className="w-4 h-4" />
          Export PDF
        </Button>
      </div>

      <div
        className="flex gap-1 overflow-x-auto border-b border-border"
        data-testid="stats-sub-tabs"
      >
        {tabs.map((t) => {
          const Icon = t.icon;
          const selected = t.id === statsTab;
          return (
            <button
              key={t.id}
              type="button"
              onClick={() => setStatsTab(t.id)}
              className={
                "shrink-0 inline-flex items-center gap-2 px-4 py-2.5 text-sm font-semibold border-b-2 -mb-px transition-colors " +
                (selected
                  ? "border-primary text-primary"
                  : "border-transparent text-muted-foreground hover:text-foreground")
              }
              data-testid={`stats-tab-${t.id}`}
            >
              <Icon className="w-4 h-4" />
              {t.label}
            </button>
          );
        })}
      </div>

      <div className="rounded-md border border-border bg-muted/20 px-4 py-3">
        <div className="text-sm font-semibold text-foreground">
          {active.label}
        </div>
        <div className="text-xs text-muted-foreground mt-0.5">{active.hint}</div>
      </div>

      {statsTab === "weight" && <WeightTrendChart userId={userId} />}
      {statsTab === "water" && <WaterIntakeChart userId={userId} />}
      {statsTab === "nutrition" && (
        <DietIntakeCharts userId={userId} planType={planType} />
      )}
    </div>
  );
};

const isoDateOnly = (d) => {
  try {
    return new Date(d).toISOString().slice(0, 10);
  } catch {
    return "";
  }
};

const DateExtendPicker = ({ value, onChange, disabled }) => (
  <Popover>
    <PopoverTrigger asChild>
      <Button type="button" variant="outline" size="sm" disabled={disabled}>
        Extend
      </Button>
    </PopoverTrigger>
    <PopoverContent className="p-0 w-auto" align="end">
      <Calendar
        mode="single"
        selected={value ? new Date(value) : undefined}
        onSelect={(d) => {
          if (!d) return;
          onChange?.(isoDateOnly(d));
        }}
        initialFocus
      />
    </PopoverContent>
  </Popover>
);

const toDateInputValue = (value) => {
  if (!value) return "";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "";
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};

const PersonalInfoTab = ({ customer, onCustomerUpdated, canEdit = true }) => {
  const [groups, setGroups] = useState([]);
  const [masterMealTimes, setMasterMealTimes] = useState([]);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);

  useEffect(() => {
    const fetchGroups = async () => {
      try {
        const res = await companyAdminCustomersAPI.listGroups({
          include_inactive: false,
        });
        setGroups(res.data.groups || []);
      } catch {
        setGroups([]);
      }
    };
    const fetchMealTimes = async () => {
      try {
        const res = await companyAdminCustomersAPI.getMealTimes();
        setMasterMealTimes(res.data.meal_times || []);
      } catch {
        setMasterMealTimes([]);
      }
    };
    fetchGroups();
    fetchMealTimes();
  }, []);

  const [saving, setSaving] = useState(false);

  const [form, setForm] = useState(() => ({
    name: customer.name || "",
    email: customer.email || "",
    new_password: "",
    phone_number: customer.phone_number || "",
    country: customer.country || "",
    city: customer.city || "",
    address: customer.address || "",
    date_of_birth: toDateInputValue(customer.date_of_birth),
    profile_photo: customer.profile_photo || "",
    emergency_contact: customer.emergency_contact || "",
    preferred_language: customer.preferred_language || "",
    occupation: customer.occupation || "",
    smoking_status: customer.smoking_status || "",
    alcohol_status: customer.alcohol_status || "",

    ai_plan_price:
      (customer.ai_plan_price ?? customer.ai_plan_price === 0)
        ? String(customer.ai_plan_price)
        : "",
    ai_plan: Number(customer.ai_plan ?? 0),
    ai_plan_expiry: customer.ai_plan_expiry || "",

    premium_member_price:
      (customer.premium_member_price ?? customer.premium_member_price === 0)
        ? String(customer.premium_member_price)
        : "",
    premium_member: Number(customer.premium_member ?? 0),
    expiry_date: customer.expiry_date || "",

    allow_openai: Number(customer.allow_openai ?? 0),
    allow_mic: Number(customer.allow_mic ?? 0),

    group_id: customer.group_id ?? customer.group?.id ?? "",
    reminder: customer.reminder || "yes",
    reminder_type: customer.reminder_type || "message",
    reminder_mealtime: customer.reminder_mealtime || {},
  }));

  useEffect(() => {
    setForm({
      name: customer.name || "",
      email: customer.email || "",
      new_password: "",
      phone_number: customer.phone_number || "",
      country: customer.country || "",
      city: customer.city || "",
      address: customer.address || "",
      date_of_birth: toDateInputValue(customer.date_of_birth),
      profile_photo: customer.profile_photo || "",
      emergency_contact: customer.emergency_contact || "",
      preferred_language: customer.preferred_language || "",
      occupation: customer.occupation || "",
      smoking_status: customer.smoking_status || "",
      alcohol_status: customer.alcohol_status || "",

      ai_plan_price:
        (customer.ai_plan_price ?? customer.ai_plan_price === 0)
          ? String(customer.ai_plan_price)
          : "",
      ai_plan: Number(customer.ai_plan ?? 0),
      ai_plan_expiry: customer.ai_plan_expiry || "",

      premium_member_price:
        (customer.premium_member_price ?? customer.premium_member_price === 0)
          ? String(customer.premium_member_price)
          : "",
      premium_member: Number(customer.premium_member ?? 0),
      expiry_date: customer.expiry_date || "",

      allow_openai: Number(customer.allow_openai ?? 0),
      allow_mic: Number(customer.allow_mic ?? 0),

      group_id: customer.group_id ?? customer.group?.id ?? "",
      reminder: customer.reminder || "yes",
      reminder_type: customer.reminder_type || "message",
      reminder_mealtime: customer.reminder_mealtime || {},
    });
  }, [customer]);

  const handlePhotoUpload = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    const customerId = customer.user_id || customer._id;
    setUploadingPhoto(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await companyAdminCustomersAPI.uploadProfilePhoto(
        customerId,
        fd,
      );
      const photo = res.data.profile_photo || "";
      setForm((p) => ({ ...p, profile_photo: photo }));
      onCustomerUpdated(res.data);
      toast.success("Profile photo updated");
    } catch (err) {
      toast.error(
        err.response?.data?.message ||
          err.response?.data?.detail ||
          "Failed to upload profile photo",
      );
    } finally {
      setUploadingPhoto(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = {
        name: form.name,
        phone_number: form.phone_number,
        country: form.country,
        city: form.city,
        address: form.address,
        date_of_birth: form.date_of_birth || null,
        profile_photo: form.profile_photo || null,
        emergency_contact: form.emergency_contact || null,
        preferred_language: form.preferred_language || null,
        occupation: form.occupation || null,
        smoking_status: form.smoking_status || null,
        alcohol_status: form.alcohol_status || null,

        ai_plan_price: form.ai_plan_price === "" ? "" : form.ai_plan_price,
        ai_plan: Number(form.ai_plan),
        ai_plan_expiry: form.ai_plan === 1 ? form.ai_plan_expiry || "" : "",

        premium_member_price:
          form.premium_member_price === "" ? "" : form.premium_member_price,
        premium_member: Number(form.premium_member),
        expiry_date: form.premium_member === 1 ? form.expiry_date || "" : "",

        allow_openai: Number(form.allow_openai),
        allow_mic: Number(form.allow_mic),

        group_id: form.group_id === "" ? null : form.group_id,

        reminder: form.reminder,
        reminder_type: form.reminder_type,
        reminder_mealtime: form.reminder_mealtime,

        ...(form.new_password.trim()
          ? { new_password: form.new_password.trim() }
          : {}),
      };

      const customerId = customer.user_id || customer._id;
      const res = await companyAdminCustomersAPI.updateBasic(
        customerId,
        payload,
      );
      onCustomerUpdated(res.data);
      toast.success("Personal info updated");
      setForm((p) => ({ ...p, new_password: "" }));
    } catch (e) {
      toast.error(
        e.response?.data?.message ||
          e.response?.data?.detail ||
          "Failed to update personal info",
      );
    } finally {
      setSaving(false);
    }
  };

  const photoUrl = buildBrandingUrl(form.profile_photo);
  const [personalSubTab, setPersonalSubTab] = useState("profile");

  const personalSubTabs = [
    { id: "profile", label: "Profile", icon: User },
    { id: "account", label: "Account", icon: IdCard },
    { id: "reminders", label: "Reminders", icon: Bell },
    { id: "membership", label: "Membership", icon: CreditCard },
  ];

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <div className="text-sm font-semibold text-foreground">
            Personal Info
          </div>
          <div className="text-xs text-muted-foreground mt-1">
            Update personal details, account, reminders, and membership.
          </div>
        </div>
        {canEdit && (
          <Button onClick={handleSave} disabled={saving}>
            {saving ? "Saving..." : "Save"}
          </Button>
        )}
      </div>

      <Tabs
        tabs={personalSubTabs}
        activeTab={personalSubTab}
        onChange={setPersonalSubTab}
        className="mb-1"
      />

      {personalSubTab === "profile" && (
        <div className="rounded-md border border-border p-4">
          <div className="text-sm font-semibold text-foreground">
            Profile details
          </div>
          <div className="mt-4 flex flex-col sm:flex-row gap-4 items-start">
            <div className="shrink-0">
              <Label className="text-foreground">Profile photo</Label>
              <div className="mt-2 flex items-center gap-3">
                <div className="h-20 w-20 rounded-full border border-border bg-muted/40 overflow-hidden flex items-center justify-center">
                  {photoUrl ? (
                    <img
                      src={photoUrl}
                      alt={form.name || "Profile"}
                      className="h-full w-full object-cover"
                    />
                  ) : (
                    <User className="h-8 w-8 text-muted-foreground" />
                  )}
                </div>
                {canEdit && (
                  <div>
                    <label className="inline-flex">
                      <input
                        type="file"
                        accept="image/*"
                        className="hidden"
                        onChange={handlePhotoUpload}
                        disabled={uploadingPhoto}
                      />
                      <span className="inline-flex items-center h-9 px-3 rounded-md border border-border text-sm cursor-pointer hover:bg-muted/40">
                        {uploadingPhoto ? "Uploading..." : "Upload photo"}
                      </span>
                    </label>
                    {form.profile_photo && (
                      <button
                        type="button"
                        className="block mt-1 text-xs text-muted-foreground hover:text-foreground"
                        onClick={() =>
                          setForm((p) => ({ ...p, profile_photo: "" }))
                        }
                      >
                        Remove
                      </button>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
            <div>
              <Label className="text-foreground">Date of birth</Label>
              <Input
                type="date"
                value={form.date_of_birth}
                onChange={(e) =>
                  setForm({ ...form, date_of_birth: e.target.value })
                }
                disabled={!canEdit}
              />
            </div>
            <div>
              <Label className="text-foreground">Emergency contact</Label>
              <Input
                value={form.emergency_contact}
                onChange={(e) =>
                  setForm({ ...form, emergency_contact: e.target.value })
                }
                placeholder="Name / phone"
                disabled={!canEdit}
              />
            </div>
            <div>
              <Label className="text-foreground">Preferred language</Label>
              <Input
                value={form.preferred_language}
                onChange={(e) =>
                  setForm({ ...form, preferred_language: e.target.value })
                }
                placeholder="e.g. English, Arabic"
                disabled={!canEdit}
              />
            </div>
            <div>
              <Label className="text-foreground">Occupation</Label>
              <Input
                value={form.occupation}
                onChange={(e) =>
                  setForm({ ...form, occupation: e.target.value })
                }
                disabled={!canEdit}
              />
            </div>
            <div>
              <Label className="text-foreground">Smoking status</Label>
              <select
                className="form-select"
                value={form.smoking_status}
                onChange={(e) =>
                  setForm({ ...form, smoking_status: e.target.value })
                }
                disabled={!canEdit}
              >
                <option value="">Not set</option>
                <option value="never">Never</option>
                <option value="former">Former</option>
                <option value="current">Current</option>
                <option value="prefer_not_say">Prefer not to say</option>
              </select>
            </div>
            <div>
              <Label className="text-foreground">Alcohol status</Label>
              <select
                className="form-select"
                value={form.alcohol_status}
                onChange={(e) =>
                  setForm({ ...form, alcohol_status: e.target.value })
                }
                disabled={!canEdit}
              >
                <option value="">Not set</option>
                <option value="never">Never</option>
                <option value="occasional">Occasional</option>
                <option value="regular">Regular</option>
                <option value="prefer_not_say">Prefer not to say</option>
              </select>
            </div>
          </div>
        </div>
      )}

      {personalSubTab === "account" && (
        <div className="rounded-md border border-border p-4">
          <div className="text-sm font-semibold text-foreground">
            Account details
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
            <div>
              <Label className="text-foreground">Name</Label>
              <Input
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                disabled={!canEdit}
              />
            </div>
            <div>
              <Label className="text-foreground">Email</Label>
              <Input value={form.email} disabled />
            </div>
            <div>
              <Label className="text-foreground">Password</Label>
              <Input
                type="password"
                value={form.new_password}
                onChange={(e) =>
                  setForm({ ...form, new_password: e.target.value })
                }
                placeholder="Set new password"
                disabled={!canEdit}
              />
            </div>

            <div>
              <Label className="text-foreground">Select Group</Label>
              <select
                className="form-select"
                value={form.group_id}
                onChange={(e) => setForm({ ...form, group_id: e.target.value })}
                disabled={!canEdit}
              >
                <option value="">No Group</option>
                {(groups || []).map((g) => (
                  <option key={g.id} value={g.id}>
                    {g.name}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <Label className="text-foreground">Phone</Label>
              <Input
                value={form.phone_number}
                onChange={(e) =>
                  setForm({ ...form, phone_number: e.target.value })
                }
                disabled={!canEdit}
              />
            </div>

            <div>
              <Label className="text-foreground">User ID</Label>
              <Input value={String(customer.user_id)} disabled />
            </div>
            <div>
              <Label className="text-foreground">Country</Label>
              <Input
                value={form.country}
                onChange={(e) => setForm({ ...form, country: e.target.value })}
                disabled={!canEdit}
              />
            </div>
            <div>
              <Label className="text-foreground">City</Label>
              <Input
                value={form.city}
                onChange={(e) => setForm({ ...form, city: e.target.value })}
                disabled={!canEdit}
              />
            </div>
            <div>
              <Label className="text-foreground">Timezone</Label>
              <Input value={customer.timezone || "-"} disabled />
            </div>
            <div className="md:col-span-2">
              <Label className="text-foreground">Address</Label>
              <Input
                value={form.address}
                onChange={(e) => setForm({ ...form, address: e.target.value })}
                disabled={!canEdit}
              />
            </div>
          </div>
        </div>
      )}

      {personalSubTab === "reminders" && (
        <div className="space-y-4">
          <div className="rounded-md border border-border p-4">
            <div className="text-sm font-semibold text-foreground">
              Reminder settings
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
              <div>
                <Label className="text-foreground">Reminder</Label>
                <select
                  className="form-select"
                  value={form.reminder}
                  onChange={(e) =>
                    setForm({ ...form, reminder: e.target.value })
                  }
                  disabled={!canEdit}
                  data-testid="reminder-select"
                >
                  <option value="yes">Yes</option>
                  <option value="no">No</option>
                </select>
              </div>

              <div>
                <Label className="text-foreground">Reminder Type</Label>
                <select
                  className="form-select"
                  value={form.reminder_type}
                  onChange={(e) =>
                    setForm({ ...form, reminder_type: e.target.value })
                  }
                  disabled={!canEdit}
                  data-testid="reminder-type-select"
                >
                  <option value="message">Message</option>
                  <option value="call">Call</option>
                </select>
              </div>
            </div>
          </div>

          <div className="rounded-md border border-border p-4">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-sm font-semibold text-foreground">
                  Reminder Meal Times
                </div>
                <div className="text-xs text-muted-foreground mt-0.5">
                  Enable reminders for specific meal times and set when to send
                  them.
                </div>
              </div>
              {Object.keys(form.reminder_mealtime || {}).length > 0 && (
                <span className="text-xs bg-primary/10 text-primary px-2 py-0.5 rounded-full font-medium">
                  {Object.keys(form.reminder_mealtime).length} active
                </span>
              )}
            </div>

            {(() => {
              const customerMT = customer.meal_times || {};
              const assignedIds = Object.keys(customerMT);

              if (assignedIds.length === 0) {
                return (
                  <div className="mt-4 text-sm text-muted-foreground italic">
                    No meal times assigned to this customer yet.
                  </div>
                );
              }

              const rows = assignedIds
                .map((idStr) => {
                  const master = masterMealTimes.find(
                    (m) => String(m.id) === idStr,
                  );
                  return {
                    id: idStr,
                    name: master ? master.name : "Meal Time " + idStr,
                    scheduledTime: customerMT[idStr],
                  };
                })
                .sort((a, b) => Number(a.id) - Number(b.id));

              const isEnabled = (id) => id in (form.reminder_mealtime || {});

              const handleToggle = (id, scheduledTime) => {
                setForm((f) => {
                  const prev = { ...(f.reminder_mealtime || {}) };
                  if (id in prev) {
                    delete prev[id];
                  } else {
                    prev[id] = scheduledTime;
                  }
                  return { ...f, reminder_mealtime: prev };
                });
              };

              const handleTimeChange = (id, val) => {
                setForm((f) => ({
                  ...f,
                  reminder_mealtime: {
                    ...(f.reminder_mealtime || {}),
                    [id]: val,
                  },
                }));
              };

              return (
                <div className="mt-4 space-y-2">
                  {rows.map((row) => {
                    const enabled = isEnabled(row.id);
                    return (
                      <div
                        key={row.id}
                        data-testid={"reminder-meal-row-" + row.id}
                        className={
                          "flex items-center gap-3 px-3 py-2.5 rounded-lg border transition-all " +
                          (enabled
                            ? "border-primary/40 bg-primary/5"
                            : "border-border bg-muted/20")
                        }
                      >
                        <input
                          type="checkbox"
                          id={"rmt-" + row.id}
                          checked={enabled}
                          onChange={() =>
                            handleToggle(row.id, row.scheduledTime)
                          }
                          disabled={!canEdit}
                          className="w-4 h-4 accent-primary cursor-pointer"
                          data-testid={"reminder-meal-toggle-" + row.id}
                        />

                        <label
                          htmlFor={"rmt-" + row.id}
                          className="flex-1 min-w-0 cursor-pointer"
                        >
                          <span
                            className={
                              "text-sm font-medium " +
                              (enabled
                                ? "text-foreground"
                                : "text-muted-foreground")
                            }
                          >
                            {row.name}
                          </span>
                          <span className="text-xs text-muted-foreground ml-2">
                            (scheduled {row.scheduledTime})
                          </span>
                        </label>

                        {enabled ? (
                          <div className="flex items-center gap-2 shrink-0">
                            <span className="text-xs text-muted-foreground">
                              Remind at
                            </span>
                            <input
                              type="time"
                              value={
                                (form.reminder_mealtime || {})[row.id] ||
                                row.scheduledTime
                              }
                              onChange={(e) =>
                                handleTimeChange(row.id, e.target.value)
                              }
                              disabled={!canEdit}
                              className="border border-primary/40 bg-card rounded-md px-2 py-1 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-primary/30"
                              data-testid={"reminder-time-" + row.id}
                            />
                          </div>
                        ) : (
                          <span className="text-xs text-muted-foreground shrink-0 italic">
                            disabled
                          </span>
                        )}
                      </div>
                    );
                  })}
                </div>
              );
            })()}
          </div>
        </div>
      )}

      {personalSubTab === "membership" && (
        <div className="rounded-md border border-border p-4">
          <div className="text-sm font-semibold text-foreground">Membership</div>
          <div className="mt-3 flex flex-wrap items-center gap-3 p-3 rounded-md bg-muted/40 border border-border">
            <CustomerMembershipBadge customer={customer} size="lg" showLabel />
            <div className="text-sm text-muted-foreground">
              <span className="text-muted-foreground">On trial:</span>{" "}
              <span className="font-medium text-foreground">
                {customer.on_trial != null
                  ? customer.on_trial
                    ? "Yes"
                    : "No"
                  : Number(customer.trial) === 1
                    ? "Yes"
                    : "No"}
              </span>
              {customer.on_trial && customer.trial_days_left != null && (
                <span className="ml-2 text-violet-700 dark:text-violet-300 font-medium">
                  (
                  {customer.trial_days_left === 0
                    ? "ends today"
                    : `${customer.trial_days_left} days left`}
                  )
                </span>
              )}
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
            <div>
              <Label className="text-foreground">Trial (system)</Label>
              <div className="mt-1.5 text-sm font-medium text-foreground">
                {Number(customer.trial) === 1 ? "Yes (active flag)" : "No"}
              </div>
              <p className="text-xs text-muted-foreground mt-0.5">
                Trial end uses AI plan expiry field until customer subscribes.
              </p>
            </div>
            <div>
              <Label className="text-foreground">AI plan Price</Label>
              <Input
                value={form.ai_plan_price}
                onChange={(e) =>
                  setForm({ ...form, ai_plan_price: e.target.value })
                }
                disabled={!canEdit}
              />
            </div>
            <div>
              <Label className="text-foreground">AI status</Label>
              <select
                className="form-select"
                value={form.ai_plan}
                onChange={(e) =>
                  setForm({ ...form, ai_plan: Number(e.target.value) })
                }
                disabled={!canEdit}
              >
                <option value={0}>No</option>
                <option value={1}>Yes</option>
              </select>
              {form.ai_plan === 1 && (
                <div className="flex items-center justify-between gap-3 mt-2">
                  <div className="text-xs text-muted-foreground">
                    Expiry:{" "}
                    <span className="text-foreground font-semibold">
                      {form.ai_plan_expiry || "-"}
                    </span>
                  </div>
                  <DateExtendPicker
                    value={form.ai_plan_expiry}
                    onChange={(v) => setForm({ ...form, ai_plan_expiry: v })}
                    disabled={!canEdit}
                  />
                </div>
              )}
            </div>

            <div>
              <Label className="text-foreground">
                Premium Membership Price
              </Label>
              <Input
                value={form.premium_member_price}
                onChange={(e) =>
                  setForm({ ...form, premium_member_price: e.target.value })
                }
                disabled={!canEdit}
              />
            </div>
            <div>
              <Label className="text-foreground">
                Premium Membership Status
              </Label>
              <select
                className="form-select"
                value={form.premium_member}
                onChange={(e) =>
                  setForm({
                    ...form,
                    premium_member: Number(e.target.value),
                  })
                }
                disabled={!canEdit}
              >
                <option value={0}>No</option>
                <option value={1}>Yes</option>
              </select>
              {form.premium_member === 1 && (
                <div className="flex items-center justify-between gap-3 mt-2">
                  <div className="text-xs text-muted-foreground">
                    Expiry:{" "}
                    <span className="text-foreground font-semibold">
                      {form.expiry_date || "-"}
                    </span>
                  </div>
                  <DateExtendPicker
                    value={form.expiry_date}
                    onChange={(v) => setForm({ ...form, expiry_date: v })}
                    disabled={!canEdit}
                  />
                </div>
              )}
            </div>

            <div>
              <Label className="text-foreground">Allow Open AI</Label>
              <select
                className="form-select"
                value={form.allow_openai}
                onChange={(e) =>
                  setForm({ ...form, allow_openai: Number(e.target.value) })
                }
                disabled={!canEdit}
              >
                <option value={0}>No</option>
                <option value={1}>Yes</option>
              </select>
            </div>
            <div>
              <Label className="text-foreground">Allow Mic</Label>
              <select
                className="form-select"
                value={form.allow_mic}
                onChange={(e) =>
                  setForm({ ...form, allow_mic: Number(e.target.value) })
                }
                disabled={!canEdit}
              >
                <option value={0}>No</option>
                <option value={1}>Yes</option>
              </select>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

/** WHO adult BMI from survey height/weight inputs. Height may be cm or decimal ft. */
const computeSurveyBmi = (heightValue, heightUnit, weightValue, weightUnit) => {
  const h = Number(heightValue);
  const w = Number(weightValue);
  if (!Number.isFinite(h) || !Number.isFinite(w) || h <= 0 || w <= 0) {
    return null;
  }
  const weightKg =
    String(weightUnit || "kg").toLowerCase() === "lb" ||
    String(weightUnit || "").toLowerCase() === "lbs"
      ? w * 0.45359237
      : w;
  let heightM;
  const hu = String(heightUnit || "cm").toLowerCase();
  if (hu === "ft" || hu === "feet") {
    heightM = h * 0.3048;
  } else {
    heightM = h / 100;
  }
  if (heightM <= 0) return null;
  const bmi = weightKg / (heightM * heightM);
  if (!Number.isFinite(bmi) || bmi <= 0) return null;
  let category = "Normal";
  if (bmi < 18.5) category = "Underweight";
  else if (bmi < 25) category = "Normal";
  else if (bmi < 30) category = "Overweight";
  else category = "Obese";
  return { value: Math.round(bmi * 10) / 10, category };
};

const SurveyTab = ({ customer, onCustomerUpdated, canEdit = true }) => {
  const [saving, setSaving] = useState(false);
  const buildForm = (c) => {
    const s = c?.survey || {};
    return {
      dietitian_consultation: s.dietitian_consultation ?? 1,
      age: s.age ?? "",
      gender: s.gender ?? "",
      diet_plan_type: s.diet_plan_type ?? 3,
      height_unit: s.height_unit ?? "ft",
      height_cm: s.height_cm ?? s.height_ft ?? "",
      weight_unit: s.weight_unit ?? "kg",
      weight_kg: s.weight_kg ?? "",
      target_weight: s.target_weight ?? "",
      goal: s.goal ?? "",
      lifestyle: s.lifestyle ?? "",
      health_issues: normalizeCommaSeparated(c?.health_issues || ""),
      medications: s.medications || "",
      food_allergies: s.food_allergies || "",
      family_history: s.family_history || "",
    };
  };
  const [form, setForm] = useState(() => buildForm(customer));

  useEffect(() => {
    setForm(buildForm(customer));
  }, [customer]);

  const bmiInfo = useMemo(
    () =>
      computeSurveyBmi(
        form.height_cm,
        form.height_unit,
        form.weight_kg,
        form.weight_unit,
      ),
    [form.height_cm, form.height_unit, form.weight_kg, form.weight_unit],
  );

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = {
        dietitian_consultation: Number(form.dietitian_consultation),
        age: form.age === "" ? null : Number(form.age),
        gender: form.gender || null,
        diet_plan_type: Number(form.diet_plan_type),
        height_unit: form.height_unit,
        height_cm: form.height_cm === "" ? null : Number(form.height_cm),
        weight_unit: form.weight_unit,
        weight_kg: form.weight_kg === "" ? null : Number(form.weight_kg),
        target_weight:
          form.target_weight === "" ? null : Number(form.target_weight),
        goal: form.goal || null,
        lifestyle: form.lifestyle || null,
        health_issues: normalizeCommaSeparated(form.health_issues),
        medications: form.medications || null,
        food_allergies: form.food_allergies || null,
        family_history: form.family_history || null,
      };

      const res = await companyAdminCustomersAPI.updateSurvey(
        customer.user_id,
        payload,
      );
      const updatedSurvey = res.data.survey;
      onCustomerUpdated({
        ...customer,
        survey: updatedSurvey,
        health_issues:
          res.data.health_issues !== undefined
            ? res.data.health_issues
            : form.health_issues,
      });
      toast.success("Survey updated");
    } catch (e) {
      toast.error(
        e.response?.data?.message ||
          e.response?.data?.detail ||
          "Failed to update survey",
      );
    } finally {
      setSaving(false);
    }
  };

  const bmiTone =
    bmiInfo?.category === "Underweight"
      ? "border-sky-200 bg-sky-50 text-sky-900"
      : bmiInfo?.category === "Overweight"
        ? "border-amber-200 bg-amber-50 text-amber-900"
        : bmiInfo?.category === "Obese"
          ? "border-red-200 bg-red-50 text-red-900"
          : bmiInfo
            ? "border-emerald-200 bg-emerald-50 text-emerald-900"
            : "border-border bg-muted/30 text-foreground";

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-4">
        <div>
          <div className="text-sm font-semibold text-foreground">Survey</div>
          <div className="text-xs text-muted-foreground mt-1">
            Height/weight drive BMI automatically. Clinical notes help before
            meal planning.
          </div>
        </div>
        {canEdit && (
          <Button onClick={handleSave} disabled={saving}>
            {saving ? "Saving..." : "Save"}
          </Button>
        )}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <div className={`rounded-md border p-4 ${bmiTone}`}>
          <div className="text-xs opacity-70">BMI</div>
          <div className="text-xl font-semibold mt-1" data-testid="survey-bmi">
            {bmiInfo ? bmiInfo.value : "—"}
          </div>
          <div className="text-xs mt-1">
            {bmiInfo
              ? `${bmiInfo.category}`
              : "Enter height & weight to calculate"}
          </div>
        </div>
        <div className="rounded-md border border-border bg-card p-4">
          <div className="text-xs text-muted-foreground">Height</div>
          <div className="text-xl font-semibold text-foreground mt-1">
            {form.height_cm !== "" && form.height_cm != null
              ? `${form.height_cm} ${String(form.height_unit || "").toUpperCase()}`
              : "—"}
          </div>
        </div>
        <div className="rounded-md border border-border bg-card p-4">
          <div className="text-xs text-muted-foreground">Weight</div>
          <div className="text-xl font-semibold text-foreground mt-1">
            {form.weight_kg !== "" && form.weight_kg != null
              ? `${form.weight_kg} ${String(form.weight_unit || "").toUpperCase()}`
              : "—"}
          </div>
        </div>
      </div>

      {bmiInfo && (
        <div
          className={`rounded-md border px-4 py-3 text-sm font-medium ${bmiTone}`}
          data-testid="survey-bmi-summary"
        >
          BMI {bmiInfo.value} ({bmiInfo.category})
        </div>
      )}

      <div className="rounded-md border border-border p-4 space-y-4">
        <div className="text-sm font-semibold text-foreground">
          Health & clinical notes
        </div>
        <div>
          <Label className="text-foreground">Health issues</Label>
          <Textarea
            value={form.health_issues}
            onChange={(e) =>
              setForm({ ...form, health_issues: e.target.value })
            }
            placeholder="e.g., hypothyroidism, hypertension"
            disabled={!canEdit}
            data-testid="survey-health-issues"
          />
        </div>
        <div>
          <Label className="text-foreground">Food allergies</Label>
          <Textarea
            value={form.food_allergies}
            onChange={(e) =>
              setForm({ ...form, food_allergies: e.target.value })
            }
            placeholder="e.g., peanuts, shellfish"
            disabled={!canEdit}
            data-testid="survey-food-allergies"
          />
        </div>
        <div>
          <Label className="text-foreground">Medications</Label>
          <Textarea
            value={form.medications}
            onChange={(e) => setForm({ ...form, medications: e.target.value })}
            placeholder="e.g., Levothyroxine 50mcg"
            disabled={!canEdit}
            data-testid="survey-medications"
          />
        </div>
        <div>
          <Label className="text-foreground">Family history</Label>
          <Textarea
            value={form.family_history}
            onChange={(e) =>
              setForm({ ...form, family_history: e.target.value })
            }
            placeholder="e.g., Type 2 Diabetes"
            disabled={!canEdit}
            data-testid="survey-family-history"
          />
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <Label className="text-foreground">Diet Plan Type</Label>
          <select
            className="form-select"
            value={form.diet_plan_type}
            onChange={(e) =>
              setForm({ ...form, diet_plan_type: e.target.value })
            }
            data-testid="survey-diet-plan-type"
            disabled={!canEdit}
          >
            {dietPlanTypeOptions.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </div>

        <div>
          <Label className="text-foreground">Dietitian Consultation</Label>
          <select
            className="form-select"
            value={form.dietitian_consultation}
            onChange={(e) =>
              setForm({ ...form, dietitian_consultation: e.target.value })
            }
            disabled={!canEdit}
          >
            <option value={1}>Yes</option>
            <option value={0}>No</option>
          </select>
        </div>

        <div>
          <Label className="text-foreground">Age</Label>
          <Input
            value={form.age}
            onChange={(e) => setForm({ ...form, age: e.target.value })}
            disabled={!canEdit}
          />
        </div>

        <div>
          <Label className="text-foreground">Gender</Label>
          <select
            className="form-select"
            value={form.gender}
            onChange={(e) => setForm({ ...form, gender: e.target.value })}
            data-testid="survey-gender"
            disabled={!canEdit}
          >
            <option value="">Select</option>
            {genderOptions.map((o) => (
              <option key={o} value={o}>
                {o}
              </option>
            ))}
          </select>
        </div>

        <div>
          <Label className="text-foreground">Height Unit</Label>
          <select
            className="form-select"
            value={form.height_unit}
            onChange={(e) => setForm({ ...form, height_unit: e.target.value })}
            data-testid="survey-height-unit"
            disabled={!canEdit}
          >
            {heightUnitOptions.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </div>

        <div>
          <Label className="text-foreground">Height</Label>
          <Input
            value={form.height_cm}
            onChange={(e) => setForm({ ...form, height_cm: e.target.value })}
            data-testid="survey-height"
            disabled={!canEdit}
            placeholder={form.height_unit === "ft" ? "e.g. 5.4" : "e.g. 165"}
          />
        </div>

        <div>
          <Label className="text-foreground">Weight Unit</Label>
          <select
            className="form-select"
            value={form.weight_unit}
            onChange={(e) => setForm({ ...form, weight_unit: e.target.value })}
            data-testid="survey-weight-unit"
            disabled={!canEdit}
          >
            {weightUnitOptions.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </div>

        <div>
          <Label className="text-foreground">Weight</Label>
          <Input
            value={form.weight_kg}
            onChange={(e) => setForm({ ...form, weight_kg: e.target.value })}
            data-testid="survey-weight"
            disabled={!canEdit}
            placeholder={form.weight_unit === "lb" ? "e.g. 160" : "e.g. 73"}
          />
        </div>

        <div>
          <Label className="text-foreground">Target Weight</Label>
          <Input
            value={form.target_weight}
            onChange={(e) =>
              setForm({ ...form, target_weight: e.target.value })
            }
            disabled={!canEdit}
          />
        </div>

        <div>
          <Label className="text-foreground">Goal</Label>
          <select
            className="form-select"
            value={form.goal}
            onChange={(e) => setForm({ ...form, goal: e.target.value })}
            data-testid="survey-goal"
            disabled={!canEdit}
          >
            <option value="">Select</option>
            {goalOptions.map((o) => (
              <option key={o} value={o}>
                {o}
              </option>
            ))}
          </select>
        </div>

        <div className="md:col-span-2">
          <Label className="text-foreground">Lifestyle</Label>
          <select
            className="form-select"
            value={form.lifestyle}
            onChange={(e) => setForm({ ...form, lifestyle: e.target.value })}
            data-testid="survey-lifestyle"
            disabled={!canEdit}
          >
            <option value="">Select</option>
            {lifestyleOptions.map((o) => (
              <option key={o} value={o}>
                {o}
              </option>
            ))}
          </select>
        </div>
      </div>
    </div>
  );
};

const budgetLevelOptions = [
  { value: "", label: "Not set" },
  { value: "low", label: "Low" },
  { value: "mid", label: "Mid level" },
  { value: "high", label: "High" },
];

const dosePerDayOptions = [
  { value: 1, label: "1 per day" },
  { value: 2, label: "2 per day" },
];

const isoDateInput = (v) => {
  if (!v) return "";
  try {
    const d = v instanceof Date ? v : new Date(v);
    if (Number.isNaN(d.getTime())) return "";
    return d.toISOString().slice(0, 10);
  } catch {
    return "";
  }
};

const formatSupplementDuration = (fromDate, tillDate) => {
  const a = isoDateInput(fromDate);
  const b = isoDateInput(tillDate);
  if (!a && !b) return "Ongoing";
  if (a && b) return `${a} → ${b}`;
  if (a) return `From ${a}`;
  return `Until ${b}`;
};

const emptySupplementForm = () => ({
  name: "",
  molecular_name: "",
  company_name: "",
  description: "",
  dose_per_day: 1,
  from_date: "",
  till_date: "",
});

const SupplementsTab = ({ customer, canEdit = true }) => {
  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptySupplementForm);
  const [saving, setSaving] = useState(false);

  const fetchItems = useCallback(async () => {
    setLoading(true);
    try {
      const res = await companyAdminCustomersAPI.supplementsList({
        user_id: customer.user_id,
      });
      setItems(res.data.supplements || []);
    } catch {
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [customer.user_id]);

  useEffect(() => {
    fetchItems();
  }, [fetchItems]);

  const openAdd = () => {
    setEditing(null);
    setForm(emptySupplementForm());
    setModalOpen(true);
  };

  const openEdit = (row) => {
    setEditing(row);
    setForm({
      name: row.name || "",
      molecular_name: row.molecular_name || "",
      company_name: row.company_name || "",
      description: row.description || "",
      dose_per_day: Number(row.dose_per_day) === 2 ? 2 : 1,
      from_date: isoDateInput(row.from_date),
      till_date: isoDateInput(row.till_date),
    });
    setModalOpen(true);
  };

  const handleSave = async () => {
    const name = String(form.name || "").trim();
    if (!name) return toast.error("Name is required");
    const dose = Number(form.dose_per_day) === 2 ? 2 : 1;

    setSaving(true);
    try {
      const payload = {
        name,
        molecular_name: String(form.molecular_name || "").trim() || null,
        company_name: String(form.company_name || "").trim() || null,
        description: String(form.description || "").trim() || null,
        dose_per_day: dose,
        from_date: form.from_date || null,
        till_date: form.till_date || null,
      };
      if (editing?.id || editing?._id) {
        await companyAdminCustomersAPI.supplementsUpdate(
          editing.id || editing._id,
          payload,
        );
        toast.success("Supplement updated");
      } else {
        await companyAdminCustomersAPI.supplementsCreate({
          user_id: customer.user_id,
          ...payload,
        });
        toast.success("Supplement added");
      }
      setModalOpen(false);
      setEditing(null);
      fetchItems();
    } catch (e) {
      toast.error(
        e.response?.data?.message ||
          e.response?.data?.detail ||
          "Failed to save supplement",
      );
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (row) => {
    const id = row?.id || row?._id;
    if (!id) return;
    // eslint-disable-next-line no-alert
    if (!window.confirm(`Delete supplement "${row.name || "this item"}"?`)) {
      return;
    }
    try {
      await companyAdminCustomersAPI.supplementsDelete(id);
      toast.success("Deleted");
      fetchItems();
    } catch (e) {
      toast.error(e.response?.data?.message || "Delete failed");
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-4">
        <div>
          <div className="text-sm font-semibold text-foreground">
            Supplements
          </div>
          <div className="text-xs text-muted-foreground mt-1">
            Coach-assigned vitamins and supplements for this customer.
          </div>
        </div>
        {canEdit && (
          <Button
            onClick={openAdd}
            className="gap-2"
            data-testid="supplement-add"
          >
            <Plus className="w-4 h-4" />
            Add Supplement
          </Button>
        )}
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-5 h-5 animate-spin text-primary" />
        </div>
      ) : items.length === 0 ? (
        <div className="text-sm text-muted-foreground">
          No supplements assigned yet.
        </div>
      ) : (
        <div className="rounded-md border border-border overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow className="bg-muted">
                <TableHead className="font-medium text-muted-foreground text-xs uppercase tracking-wider">
                  Name
                </TableHead>
                <TableHead className="font-medium text-muted-foreground text-xs uppercase tracking-wider">
                  Molecular
                </TableHead>
                <TableHead className="font-medium text-muted-foreground text-xs uppercase tracking-wider">
                  Company
                </TableHead>
                <TableHead className="font-medium text-muted-foreground text-xs uppercase tracking-wider">
                  Dose
                </TableHead>
                <TableHead className="font-medium text-muted-foreground text-xs uppercase tracking-wider">
                  Duration
                </TableHead>
                <TableHead className="font-medium text-muted-foreground text-xs uppercase tracking-wider text-right">
                  Action
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.map((row) => (
                <TableRow
                  key={row.id || row._id}
                  className="hover:bg-muted"
                >
                  <TableCell className="text-muted-foreground">
                    <div className="font-medium text-foreground">
                      {row.name || "-"}
                    </div>
                    {row.description ? (
                      <div className="text-xs text-muted-foreground mt-0.5 line-clamp-2">
                        {row.description}
                      </div>
                    ) : null}
                  </TableCell>
                  <TableCell className="text-muted-foreground">
                    {row.molecular_name || "-"}
                  </TableCell>
                  <TableCell className="text-muted-foreground">
                    {row.company_name || "-"}
                  </TableCell>
                  <TableCell className="text-muted-foreground">
                    {Number(row.dose_per_day) === 2 ? "2 per day" : "1 per day"}
                  </TableCell>
                  <TableCell className="text-muted-foreground text-sm">
                    {formatSupplementDuration(row.from_date, row.till_date)}
                  </TableCell>
                  <TableCell className="text-right">
                    {canEdit && (
                      <div className="flex justify-end gap-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          className="hover:bg-muted"
                          onClick={() => openEdit(row)}
                        >
                          <Pencil className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-destructive hover:text-destructive hover:bg-destructive/10"
                          onClick={() => handleDelete(row)}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      {modalOpen && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setModalOpen(false)} role="presentation">
          <div
            className="panel w-full max-w-lg mx-4 p-0 max-h-[75vh] app-modal-shell flex flex-col overflow-hidden"
            onClick={(e) => e.stopPropagation()}
            data-testid="supplement-modal"
          >
            <div className="app-modal-header flex items-center justify-between px-6 py-4 border-b border-border">
              <h3 className="text-lg font-semibold text-foreground">
                {editing ? "Edit Supplement" : "Add Supplement"}
              </h3>
              <button
                type="button"
                onClick={() => setModalOpen(false)}
                className="text-muted-foreground hover:text-foreground"
              >
                ✕
              </button>
            </div>
            <div className="app-modal-body p-6 space-y-4">
              <div>
                <Label className="text-foreground">Name</Label>
                <Input
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="e.g. Vitamin D3"
                  data-testid="supplement-name"
                />
              </div>
              <div>
                <Label className="text-foreground">Molecular name</Label>
                <Input
                  value={form.molecular_name}
                  onChange={(e) =>
                    setForm({ ...form, molecular_name: e.target.value })
                  }
                  placeholder="e.g. Cholecalciferol"
                />
              </div>
              <div>
                <Label className="text-foreground">Company name</Label>
                <Input
                  value={form.company_name}
                  onChange={(e) =>
                    setForm({ ...form, company_name: e.target.value })
                  }
                  placeholder="e.g. Now Foods"
                />
              </div>
              <div>
                <Label className="text-foreground">Description</Label>
                <Textarea
                  value={form.description}
                  onChange={(e) =>
                    setForm({ ...form, description: e.target.value })
                  }
                  placeholder="e.g. Take with breakfast"
                  rows={2}
                  className="resize-y"
                />
              </div>
              <div>
                <Label className="text-foreground">Dose</Label>
                <select
                  className="form-select"
                  value={form.dose_per_day}
                  onChange={(e) =>
                    setForm({
                      ...form,
                      dose_per_day: Number(e.target.value) === 2 ? 2 : 1,
                    })
                  }
                  data-testid="supplement-dose"
                >
                  {dosePerDayOptions.map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-foreground">Duration from</Label>
                  <Input
                    type="date"
                    value={form.from_date}
                    onChange={(e) =>
                      setForm({ ...form, from_date: e.target.value })
                    }
                  />
                </div>
                <div>
                  <Label className="text-foreground">Duration until</Label>
                  <Input
                    type="date"
                    value={form.till_date}
                    onChange={(e) =>
                      setForm({ ...form, till_date: e.target.value })
                    }
                  />
                </div>
              </div>
              <div className="text-xs text-muted-foreground">
                Leave dates empty for ongoing. Otherwise set start and/or end.
              </div>
            </div>
            <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-border shrink-0">
              <Button
                variant="outline"
                onClick={() => setModalOpen(false)}
                disabled={saving}
              >
                Cancel
              </Button>
              <Button
                onClick={handleSave}
                disabled={saving || !String(form.name || "").trim()}
                data-testid="supplement-save"
              >
                {saving ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Saving…
                  </>
                ) : editing ? (
                  "Save changes"
                ) : (
                  "Add supplement"
                )}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const NotesTab = ({ customer, onCustomerUpdated, canEdit = true }) => {
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(() => ({
    nutrition_diagnosis: customer.nutrition_diagnosis || "",
    weekly_budget: customer.weekly_budget || "",
    daily_budget: customer.daily_budget || "",
  }));

  useEffect(() => {
    setForm({
      nutrition_diagnosis: customer.nutrition_diagnosis || "",
      weekly_budget: customer.weekly_budget || "",
      daily_budget: customer.daily_budget || "",
    });
  }, [customer]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await companyAdminCustomersAPI.updateBasic(
        customer.user_id || customer._id,
        {
          nutrition_diagnosis: form.nutrition_diagnosis || null,
          weekly_budget: form.weekly_budget || null,
          daily_budget: form.daily_budget || null,
        },
      );
      onCustomerUpdated(res.data);
      toast.success("Customer notes updated");
    } catch (e) {
      toast.error(
        e.response?.data?.message ||
          e.response?.data?.detail ||
          "Failed to update notes",
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-4">
        <div>
          <div className="text-sm font-semibold text-foreground">
            Short Note
          </div>
          <div className="text-xs text-muted-foreground mt-1">
            Quick clinical note and budget level for this customer.
          </div>
        </div>
        {canEdit && (
          <Button onClick={handleSave} disabled={saving}>
            {saving ? "Saving..." : "Save"}
          </Button>
        )}
      </div>

      <div className="rounded-md border border-border p-4 space-y-4">
        <div>
          <Label className="text-foreground">Nutrition diagnosis</Label>
          <Textarea
            value={form.nutrition_diagnosis}
            onChange={(e) =>
              setForm({ ...form, nutrition_diagnosis: e.target.value })
            }
            placeholder="e.g., Excessive energy intake, inadequate protein"
            disabled={!canEdit}
            data-testid="note-nutrition-diagnosis"
          />
          <div className="text-xs text-muted-foreground mt-1">
            Short clinical note dietitians can use before creating a meal plan.
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label className="text-foreground">Weekly budget</Label>
            <select
              className="form-select"
              value={form.weekly_budget}
              onChange={(e) =>
                setForm({ ...form, weekly_budget: e.target.value })
              }
              disabled={!canEdit}
              data-testid="note-weekly-budget"
            >
              {budgetLevelOptions.map((o) => (
                <option key={o.value || "unset"} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </div>
          <div>
            <Label className="text-foreground">Daily budget</Label>
            <select
              className="form-select"
              value={form.daily_budget}
              onChange={(e) =>
                setForm({ ...form, daily_budget: e.target.value })
              }
              disabled={!canEdit}
              data-testid="note-daily-budget"
            >
              {budgetLevelOptions.map((o) => (
                <option key={o.value || "unset"} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>
    </div>
  );
};

const PRIORITY_COLORS = {
  high: "text-red-600 bg-red-50 border-red-200",
  medium: "text-amber-600 bg-amber-50 border-amber-200",
  low: "text-green-600 bg-green-50 border-green-200",
};

const TicketNoteModal = ({ ticket: initial, onClose, onUpdated }) => {
  const [ticket, setTicket] = React.useState(initial);
  const [note, setNote] = React.useState("");
  const [saving, setSaving] = React.useState(false);

  const handleClose = async () => {
    setSaving(true);
    try {
      const res = await ticketsAPI.close(ticket._id);
      setTicket(res.data);
      onUpdated(res.data);
      toast.success("Ticket closed");
    } catch {
      toast.error("Failed to close ticket");
    } finally {
      setSaving(false);
    }
  };

  const handleReopen = async () => {
    setSaving(true);
    try {
      const res = await ticketsAPI.reopen(ticket._id);
      setTicket(res.data);
      onUpdated(res.data);
      toast.success("Ticket reopened");
    } catch {
      toast.error("Failed to reopen ticket");
    } finally {
      setSaving(false);
    }
  };

  const handleAddNote = async () => {
    if (!note.trim()) return;
    setSaving(true);
    try {
      const res = await ticketsAPI.addNote(ticket._id, { text: note });
      setTicket(res.data);
      setNote("");
      onUpdated(res.data);
      toast.success("Note added");
    } catch {
      toast.error("Failed to add note");
    } finally {
      setSaving(false);
    }
  };

  const pri = PRIORITY_COLORS[ticket.priority] || PRIORITY_COLORS.medium;
  const isOpen = ticket.status === "open";

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={onClose} role="presentation">
      <div className="bg-card rounded-2xl shadow-2xl w-full max-w-lg app-modal-shell" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between p-5 border-b border-border">
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-xs font-mono text-muted-foreground">
                #{ticket.ticket_id}
              </span>
              <span
                className={`inline-flex items-center text-[10px] font-semibold px-2 py-0.5 rounded-full border ${pri}`}
              >
                {ticket.priority}
              </span>
              <span
                className={`inline-flex items-center text-[10px] px-2 py-0.5 rounded-full font-medium ${
                  isOpen
                    ? "bg-green-100 text-green-700"
                    : "bg-slate-100 text-slate-600"
                }`}
              >
                {isOpen ? "Open" : "Closed"}
              </span>
            </div>
            <p className="text-base font-semibold text-foreground mt-1">
              {ticket.title}
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 rounded-lg hover:bg-muted text-muted-foreground shrink-0"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="app-modal-body flex-1 overflow-y-auto min-h-0 p-5 space-y-4">
          {ticket.description && (
            <div className="bg-muted/30 rounded-lg p-3 text-sm text-foreground">
              {ticket.description}
            </div>
          )}

          <div>
            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">
              Notes ({ticket.notes?.length || 0})
            </p>
            <div className="space-y-2">
              {(ticket.notes || []).map((n, i) => (
                <div
                  key={i}
                  className="bg-blue-50 border border-blue-100 rounded-lg p-3 text-sm"
                >
                  <p className="text-foreground">{n.text}</p>
                  <p className="text-xs text-muted-foreground mt-1">
                    {n.added_by} · {new Date(n.added_at).toLocaleString()}
                  </p>
                </div>
              ))}
              {(!ticket.notes || ticket.notes.length === 0) && (
                <p className="text-xs text-muted-foreground italic">
                  No notes yet.
                </p>
              )}
            </div>
          </div>

          {isOpen && (
            <div className="flex gap-2">
              <input
                type="text"
                value={note}
                onChange={(e) => setNote(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && handleAddNote()}
                placeholder="Add a note..."
                className="flex-1 border border-border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
              />
              <button
                onClick={handleAddNote}
                disabled={saving || !note.trim()}
                className="px-3 py-2 bg-primary text-white rounded-lg text-sm font-medium disabled:opacity-50"
              >
                Add
              </button>
            </div>
          )}
        </div>

        <div className="p-4 border-t border-border flex justify-end gap-2">
          <button
            onClick={onClose}
            className="px-4 py-2 rounded-lg border border-border text-sm hover:bg-muted"
          >
            Cancel
          </button>
          {isOpen ? (
            <button
              onClick={handleClose}
              disabled={saving}
              className="px-4 py-2 rounded-lg bg-slate-700 text-white text-sm font-medium hover:bg-slate-800 disabled:opacity-50"
            >
              Close Ticket
            </button>
          ) : (
            <button
              onClick={handleReopen}
              disabled={saving}
              className="px-4 py-2 rounded-lg bg-green-600 text-white text-sm font-medium hover:bg-green-700 disabled:opacity-50"
            >
              Reopen Ticket
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

const CustomerTicketsTab = ({ customerId, customer }) => {
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusTab, setStatusTab] = useState("open");
  const [selected, setSelected] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [createForm, setCreateForm] = useState({
    title: "",
    description: "",
    priority: "medium",
  });
  const [saving, setSaving] = useState(false);

  const fetchTickets = useCallback(async () => {
    setLoading(true);
    try {
      const res = await ticketsAPI.list({ customer_ref: String(customerId) });
      setTickets(res.data.tickets || []);
    } catch {
      toast.error("Failed to load tickets");
    } finally {
      setLoading(false);
    }
  }, [customerId]);

  useEffect(() => {
    fetchTickets();
  }, [fetchTickets]);

  const handleCreate = async () => {
    if (!createForm.title.trim()) return;
    setSaving(true);
    try {
      await ticketsAPI.create({
        customer_ref: String(customerId),
        customer_name: customer?.name || customer?.full_name || "",
        customer_email: customer?.email || "",
        title: createForm.title,
        description: createForm.description,
        priority: createForm.priority,
      });
      toast.success("Ticket created");
      setShowCreate(false);
      setCreateForm({ title: "", description: "", priority: "medium" });
      fetchTickets();
    } catch {
      toast.error("Failed to create ticket");
    } finally {
      setSaving(false);
    }
  };

  const handleUpdated = (updated) => {
    setTickets((prev) =>
      prev.map((t) => (t._id === updated._id ? updated : t)),
    );
    if (selected?._id === updated._id) setSelected(updated);
  };

  const filtered = tickets.filter((t) => t.status === statusTab);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex rounded-lg border border-border overflow-hidden">
          {["open", "closed"].map((s) => (
            <button
              key={s}
              data-testid={`customer-tickets-tab-${s}`}
              onClick={() => setStatusTab(s)}
              className={`px-4 py-1.5 text-sm font-medium capitalize transition-colors ${
                statusTab === s
                  ? "bg-primary text-white"
                  : "bg-card text-muted-foreground hover:bg-muted"
              }`}
            >
              {s}
              <span className="ml-1.5 text-xs opacity-70">
                ({tickets.filter((t) => t.status === s).length})
              </span>
            </button>
          ))}
        </div>
        <Button
          onClick={() => setShowCreate(true)}
          size="sm"
          data-testid="new-ticket-btn"
        >
          <Plus className="w-4 h-4 mr-1" /> New Ticket
        </Button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-5 h-5 animate-spin text-primary" />
        </div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-12 text-muted-foreground text-sm">
          <Ticket className="w-8 h-8 mx-auto mb-2 opacity-30" />
          No {statusTab} tickets for this customer.
        </div>
      ) : (
        <div className="space-y-2">
          {filtered.map((t) => {
            const pri = PRIORITY_COLORS[t.priority] || PRIORITY_COLORS.medium;
            return (
              <div
                key={t._id}
                data-testid={`customer-ticket-${t.ticket_id}`}
                onClick={() => setSelected(t)}
                className="bg-card border border-border rounded-lg p-4 cursor-pointer hover:border-primary/30 hover:shadow-sm transition-all"
              >
                <div className="flex items-center justify-between gap-2">
                  <span className="text-xs font-mono text-muted-foreground">
                    #{t.ticket_id}
                  </span>
                  <span
                    className={`text-[10px] font-semibold px-2 py-0.5 rounded-full border ${pri}`}
                  >
                    {t.priority}
                  </span>
                </div>
                <p className="text-sm font-semibold mt-1 text-foreground">
                  {t.title}
                </p>
                {t.description && (
                  <p className="text-xs text-muted-foreground mt-1 line-clamp-2">
                    {t.description}
                  </p>
                )}
                <div className="mt-2 flex items-center gap-3 text-xs text-muted-foreground">
                  <span className="flex items-center gap-1">
                    <Clock className="w-3 h-3" />
                    {new Date(t.created_at).toLocaleDateString()}
                  </span>
                  {t.notes?.length > 0 && (
                    <span className="flex items-center gap-1">
                      <MessageSquare className="w-3 h-3" />
                      {t.notes.length}
                    </span>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {showCreate && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={() => !saving && setShowCreate(false)} role="presentation">
          <div className="bg-card rounded-2xl shadow-2xl w-full max-w-md app-modal-shell" onClick={(e) => e.stopPropagation()}>
            <div className="app-modal-header flex items-center justify-between px-6 py-4 border-b border-border">
              <h3 className="text-base font-semibold">New Ticket</h3>
              <button
                onClick={() => setShowCreate(false)}
                className="p-1 rounded hover:bg-muted"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="app-modal-body px-6 py-4 space-y-3">
              <div>
                <Label>Title *</Label>
                <Input
                  value={createForm.title}
                  onChange={(e) =>
                    setCreateForm((p) => ({ ...p, title: e.target.value }))
                  }
                  placeholder="Ticket subject..."
                  data-testid="new-ticket-title"
                />
              </div>
              <div>
                <Label>Description</Label>
                <textarea
                  value={createForm.description}
                  onChange={(e) =>
                    setCreateForm((p) => ({
                      ...p,
                      description: e.target.value,
                    }))
                  }
                  placeholder="Describe the issue..."
                  rows={3}
                  className="w-full border border-border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 resize-none"
                  data-testid="new-ticket-description"
                />
              </div>
              <div>
                <Label>Priority</Label>
                <select
                  value={createForm.priority}
                  onChange={(e) =>
                    setCreateForm((p) => ({ ...p, priority: e.target.value }))
                  }
                  className="w-full border border-border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
                  data-testid="new-ticket-priority"
                >
                  <option value="low">Low</option>
                  <option value="medium">Medium</option>
                  <option value="high">High</option>
                </select>
              </div>
            </div>
            <div className="app-modal-footer flex justify-end gap-2 px-6 py-4 border-t border-border">
              <Button variant="outline" onClick={() => setShowCreate(false)}>
                Cancel
              </Button>
              <Button
                onClick={handleCreate}
                disabled={saving || !createForm.title.trim()}
                data-testid="create-ticket-submit"
              >
                {saving ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  "Create"
                )}
              </Button>
            </div>
          </div>
        </div>
      )}

      {selected && (
        <TicketNoteModal
          ticket={selected}
          onClose={() => setSelected(null)}
          onUpdated={handleUpdated}
        />
      )}
    </div>
  );
};

// DietPlanTab moved to separate file

// Returns Tailwind text color class based on customer active plan
const getPlanNameColor = (c) => {
  if (!c) return "text-foreground";
  const now = Date.now();
  // Premium: green
  if (Number(c.premium_member) === 1) {
    const exp = c.expiry_date ? new Date(c.expiry_date).getTime() : Infinity;
    if (exp > now) return "text-green-600";
  }
  // AI Plan Paid (non-trial, active): blue
  if (
    Number(c.ai_plan) === 1 &&
    Number(c.trial) !== 1 &&
    c.ai_plan_expiry &&
    new Date(c.ai_plan_expiry).getTime() > now
  ) {
    return "text-blue-600";
  }
  // Trial: black
  if (Number(c.trial) === 1) {
    return "text-foreground";
  }
  // Unpaid / expired: red
  return "text-red-600";
};

const CustomerDetailPage = () => {
  const { id } = useParams();
  const { user } = useCompanyAdminAuth();
  const isAgent = Number(user?.user_type) === 3;
  const canEdit = !isAgent;

  const [loading, setLoading] = useState(true);
  const [customer, setCustomer] = useState(null);
  const [strictSummary, setStrictSummary] = useState({
    enabled: false,
    totals: { calories: 0, fat: 0, carbs: 0, protein: 0 },
  });
  const [moderateSummary, setModerateSummary] = useState({
    enabled: false,
    totals: { calories: 0, fat: 0, carbs: 0, protein: 0 },
  });
  const [relaxSummary, setRelaxSummary] = useState({
    enabled: false,
    totals: { calories: 0, fat: 0, carbs: 0, protein: 0 },
  });
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [activeTab, setActiveTab] = useState("personal");
  const [aiUsage, setAiUsage] = useState(null);
  const [aiUsageLoading, setAiUsageLoading] = useState(false);

  /** silent=true updates customer state without the full-page loading spinner. */
  const fetchCustomer = async ({ silent = false } = {}) => {
    if (!silent) setLoading(true);
    try {
      const res = await companyAdminCustomersAPI.getOne(id);
      setCustomer(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to fetch customer");
    } finally {
      if (!silent) setLoading(false);
    }
  };

  const refreshCustomerDetail = async () => {
    await fetchCustomer({ silent: true });
    const uid = id;
    if (uid) {
      fetchStrictSummary(uid);
      fetchModerateSummary(uid);
      fetchRelaxSummary(uid);
      fetchAiUsage(uid);
    }
  };

  const fetchStrictSummary = async (customerId) => {
    setSummaryLoading(true);
    try {
      // Backend returns enabled=false when diet_plan_type != 1
      const res = await companyAdminCustomersAPI.strictSummary({
        user_id: customerId,
      });
      setStrictSummary(
        res.data || {
          enabled: false,
          totals: { calories: 0, fat: 0, carbs: 0, protein: 0 },
        },
      );
    } catch (e) {
      setStrictSummary({
        enabled: false,
        totals: { calories: 0, fat: 0, carbs: 0, protein: 0 },
      });
    } finally {
      setSummaryLoading(false);
    }
  };

  const fetchModerateSummary = async (customerId) => {
    setSummaryLoading(true);
    try {
      // Backend returns enabled=false when diet_plan_type != 2
      const res = await companyAdminCustomersAPI.moderateSummary({
        user_id: customerId,
      });
      setModerateSummary(
        res.data || {
          enabled: false,
          totals: { calories: 0, fat: 0, carbs: 0, protein: 0 },
        },
      );
    } catch (e) {
      setModerateSummary({
        enabled: false,
        totals: { calories: 0, fat: 0, carbs: 0, protein: 0 },
      });
    } finally {
      setSummaryLoading(false);
    }
  };

  const fetchRelaxSummary = async (customerId) => {
    setSummaryLoading(true);
    try {
      // Backend returns enabled=false when diet_plan_type != 3
      const res = await companyAdminCustomersAPI.relaxSummary({
        user_id: customerId,
      });
      setRelaxSummary(
        res.data || {
          enabled: false,
          totals: { calories: 0, fat: 0, carbs: 0, protein: 0 },
        },
      );
    } catch (e) {
      setRelaxSummary({
        enabled: false,
        totals: { calories: 0, fat: 0, carbs: 0, protein: 0 },
      });
    } finally {
      setSummaryLoading(false);
    }
  };

  const fetchAiUsage = async (customerId) => {
    setAiUsageLoading(true);
    try {
      const res = await companyAdminCustomersAPI.getAiUsage(customerId);
      setAiUsage(res.data);
    } catch (e) {
      setAiUsage(null);
    } finally {
      setAiUsageLoading(false);
    }
  };

  useEffect(() => {
    fetchCustomer();
    // Fetch strict or moderate totals depending on current diet plan type.
    // Each endpoint returns enabled=false if not applicable.
    fetchStrictSummary(id);
    fetchModerateSummary(id);
    fetchRelaxSummary(id);
    fetchAiUsage(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // Refetch summaries when customer data changes (e.g., diet plan type changes)
  useEffect(() => {
    if (customer?.user_id) {
      fetchStrictSummary(customer.user_id);
      fetchModerateSummary(customer.user_id);
      fetchRelaxSummary(customer.user_id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customer?.survey?.diet_plan_type]);

  return (
    <CompanyAdminLayout>
      <div className="space-y-6" data-testid="company-admin-customer-detail">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-semibold text-foreground">
              Customer Details
            </h1>
            <p className="text-muted-foreground mt-1">#{id}</p>
          </div>
          <div className="flex items-center gap-2">
            <Button
              type="button"
              variant="outline"
              className="gap-2"
              onClick={() => refreshCustomerDetail()}
              data-testid="customer-detail-refresh"
            >
              <RefreshCw className="w-4 h-4" />
              Refresh
            </Button>
            <Link to="/admin/customers">
              <Button variant="outline" className="gap-2">
                <ArrowLeft className="w-4 h-4" />
                Back
              </Button>
            </Link>
          </div>
        </div>

        {loading ? (
          <div className="panel border border-border flex items-center justify-center py-20">
            <Loader2 className="w-6 h-6 animate-spin text-primary" />
          </div>
        ) : !customer ? (
          <div className="panel border border-border">
            <p className="text-sm text-muted-foreground">Customer not found.</p>
          </div>
        ) : (
          <div className="panel border border-border p-0 overflow-hidden">
            <div className="px-6 py-4 border-b border-border">
              <div className="flex items-start justify-between gap-4 flex-wrap">
                <div className="min-w-0 flex-1">
                  <div className="text-xs text-muted-foreground">Customer</div>
                  <div className="flex items-center gap-2 flex-wrap mt-1">
                    <div
                      className={[
                        "text-lg font-semibold",
                        getPlanNameColor(customer),
                      ].join(" ")}
                    >
                      {customer.name || customer.email}
                    </div>
                    <CustomerMembershipBadge
                      customer={customer}
                      size="lg"
                      showLabel
                    />
                  </div>
                  <div className="text-sm text-muted-foreground mt-1">
                    {customer.email}
                  </div>
                </div>
                <div className="text-right shrink-0">
                  <div className="text-xs text-muted-foreground">Account status</div>
                  <div className="text-sm font-semibold text-foreground mt-1">
                    {customer.status}
                  </div>
                </div>
              </div>
            </div>

            <Tabs
              tabs={[
                { id: "personal", label: "Personal Info", icon: User },
                { id: "survey", label: "Survey", icon: ClipboardList },
                { id: "diet", label: "Diet Plan", icon: Utensils },
                { id: "water", label: "Water Intake", icon: Droplets },
                { id: "stats", label: "Stats", icon: BarChart3 },
                {
                  id: "body-measurements",
                  label: "Body Measurements",
                  icon: Ruler,
                },
                { id: "history", label: "History", icon: Clock },
                { id: "lab-reports", label: "Lab reports", icon: FlaskConical },
                { id: "notes", label: "Notes", icon: StickyNote },
                { id: "tickets", label: "Tickets", icon: Ticket },
              ]}
              activeTab={activeTab}
              onChange={setActiveTab}
              className="px-2 border-b border-border"
            />

            {activeTab === "personal" && (
              <div
                className="px-6 py-5 border-b border-border bg-muted/40"
                data-testid="customer-personal-overview"
              >
                <div
                  className="rounded-lg border border-border bg-card p-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 text-sm"
                  data-testid="customer-membership-summary"
                >
                  <div>
                    <div className="text-xs text-muted-foreground">Membership</div>
                    <div className="font-semibold text-foreground mt-0.5">
                      {customer.membership_label || "—"}
                    </div>
                  </div>
                  <div>
                    <div className="text-xs text-muted-foreground">On trial</div>
                    <div className="font-semibold text-foreground mt-0.5">
                      {customer.on_trial ? "Yes" : "No"}
                    </div>
                  </div>
                  <div>
                    <div className="text-xs text-muted-foreground">Trial days left</div>
                    <div className="font-semibold text-foreground mt-0.5">
                      {customer.on_trial && customer.trial_days_left != null
                        ? customer.trial_days_left === 0
                          ? "Ends today"
                          : `${customer.trial_days_left} day${customer.trial_days_left === 1 ? "" : "s"}`
                        : customer.membership_status === "trial_expired"
                          ? "0 (expired)"
                          : "—"}
                    </div>
                  </div>
                  <div>
                    <div className="text-xs text-muted-foreground">Trial ends</div>
                    <div className="font-semibold text-foreground mt-0.5 text-xs">
                      {customer.trial_ends_at
                        ? new Date(customer.trial_ends_at).toLocaleString()
                        : "—"}
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4 mt-4">
                  {(() => {
                    const s = customer?.survey || {};
                    const headerBmi = computeSurveyBmi(
                      s.height_cm ?? s.height_ft,
                      s.height_unit || "cm",
                      s.weight_kg,
                      s.weight_unit || "kg",
                    );
                    return (
                      <div
                        className="rounded-md border border-border bg-card p-4"
                        data-testid="customer-bmi-card"
                      >
                        <div className="text-xs text-muted-foreground">BMI</div>
                        <div className="text-xl font-semibold text-foreground mt-2">
                          {headerBmi ? headerBmi.value : "—"}
                        </div>
                        <div className="text-xs text-muted-foreground mt-1">
                          {headerBmi
                            ? headerBmi.category
                            : "From survey height/weight"}
                        </div>
                      </div>
                    );
                  })()}
                  {[
                    { label: "Total Calories", key: "calories" },
                    { label: "Total Fat", key: "fat" },
                    { label: "Total Carbs", key: "carbs" },
                    { label: "Total Proteins", key: "protein" },
                  ].map((card) => {
                    const planType = Number(
                      customer?.survey?.diet_plan_type || 3,
                    );
                    const currentSummary =
                      planType === 3
                        ? relaxSummary
                        : planType === 2
                          ? moderateSummary
                          : strictSummary;
                    const planLabel =
                      planType === 3
                        ? "Flexible Diet Plan (Active)"
                        : planType === 2
                          ? "Moderate Diet Plan (Active)"
                          : "Strict Diet Plan (Active)";

                    return (
                      <div
                        key={card.key}
                        className="rounded-md border border-border bg-card p-4"
                      >
                        <div className="text-xs text-muted-foreground">
                          {card.label}
                        </div>
                        <div className="text-xl font-semibold text-foreground mt-2">
                          {summaryLoading
                            ? "..."
                            : currentSummary.enabled
                              ? Number(
                                  currentSummary.totals?.[card.key] || 0,
                                ).toFixed(2)
                              : "N/A"}
                        </div>
                        <div className="text-xs text-muted-foreground mt-1">
                          {planLabel}
                        </div>
                      </div>
                    );
                  })}
                </div>

                <div className="grid grid-cols-2 gap-4 mt-4">
                  <div
                    className="rounded-md border border-indigo-200 dark:border-indigo-800 bg-indigo-50 dark:bg-indigo-950/40 p-4 flex items-start gap-3"
                    data-testid="ai-count-card"
                  >
                    <BrainCircuit className="w-5 h-5 text-indigo-500 dark:text-indigo-400 mt-0.5 shrink-0" />
                    <div className="flex-1 min-w-0">
                      <div className="text-xs text-indigo-600 dark:text-indigo-300 font-medium">
                        AI Usage Today
                      </div>
                      <div
                        className="text-xl font-semibold text-indigo-900 dark:text-indigo-100 mt-1"
                        data-testid="ai-count-value"
                      >
                        {aiUsageLoading
                          ? "..."
                          : aiUsage
                            ? aiUsage.ai_limit > 0
                              ? aiUsage.today_ai_count +
                                " / " +
                                aiUsage.ai_limit
                              : String(aiUsage.today_ai_count)
                            : "0"}
                      </div>
                      <div className="text-xs text-indigo-500 dark:text-indigo-400 mt-1">
                        {aiUsage?.plan_type === "trial"
                          ? "Trial"
                          : aiUsage?.plan_type === "ai_plan"
                            ? "AI Plan"
                            : aiUsage?.plan_type === "premium"
                              ? "Premium"
                              : "Free"}
                        {aiUsage?.ai_limit > 0
                          ? " • " + aiUsage.ai_limit + " daily limit"
                          : " • Unlimited"}
                      </div>
                    </div>
                  </div>
                  <div
                    className="rounded-md border border-emerald-200 dark:border-emerald-800 bg-emerald-50 dark:bg-emerald-950/40 p-4 flex items-start gap-3"
                    data-testid="chat-count-card"
                  >
                    <MessageCircle className="w-5 h-5 text-emerald-500 dark:text-emerald-400 mt-0.5 shrink-0" />
                    <div className="flex-1 min-w-0">
                      <div className="text-xs text-emerald-600 dark:text-emerald-300 font-medium">
                        AI Chat Usage Today
                      </div>
                      <div
                        className="text-xl font-semibold text-emerald-900 dark:text-emerald-100 mt-1"
                        data-testid="chat-count-value"
                      >
                        {aiUsageLoading
                          ? "..."
                          : aiUsage
                            ? aiUsage.chat_limit > 0
                              ? aiUsage.today_chat_count +
                                " / " +
                                aiUsage.chat_limit
                              : String(aiUsage.today_chat_count)
                            : "0"}
                      </div>
                      <div className="text-xs text-emerald-500 dark:text-emerald-400 mt-1">
                        {aiUsage?.plan_type === "trial"
                          ? "Trial"
                          : aiUsage?.plan_type === "ai_plan"
                            ? "AI Plan"
                            : aiUsage?.plan_type === "premium"
                              ? "Premium"
                              : "Free"}
                        {aiUsage?.chat_limit > 0
                          ? " • " + aiUsage.chat_limit + " daily limit"
                          : " • Unlimited"}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            <div className="p-6">
              {activeTab === "personal" && (
                <PersonalInfoTab
                  customer={customer}
                  onCustomerUpdated={(updated) => {
                    if (updated) setCustomer(updated);
                    fetchCustomer({ silent: true });
                  }}
                  canEdit={canEdit}
                />
              )}

              {activeTab === "survey" && (
                <SurveyTab
                  customer={customer}
                  onCustomerUpdated={(updated) => {
                    if (updated) setCustomer(updated);
                    fetchCustomer({ silent: true });
                  }}
                  canEdit={canEdit}
                />
              )}

              {activeTab === "diet" && (
                <DietPlanTab
                  customer={customer}
                  canEdit={canEdit}
                  onSummaryRefresh={() => {
                    refreshCustomerDetail();
                  }}
                />
              )}

              {activeTab === "water" && (
                <WaterIntakeTab customer={customer} canEdit={canEdit} />
              )}

              {activeTab === "stats" && (
                <StatsPanel
                  userId={customer.user_id}
                  planType={Number(customer?.survey?.diet_plan_type || 1)}
                />
              )}

              {activeTab === "body-measurements" && (
                <BodyMeasurementsPanel
                  userId={customer.user_id}
                  canEdit={canEdit}
                />
              )}

              {activeTab === "history" && (
                <CustomerHistoryTab customerId={customer.user_id} />
              )}

              {activeTab === "lab-reports" && (
                <LabReportsTab customer={customer} canEdit={canEdit} />
              )}

              {activeTab === "notes" && (
                <NotesTab
                  customer={customer}
                  onCustomerUpdated={(updated) => {
                    if (updated) setCustomer(updated);
                    fetchCustomer({ silent: true });
                  }}
                  canEdit={canEdit}
                />
              )}

              {activeTab === "tickets" && (
                <CustomerTicketsTab
                  customerId={customer.user_id}
                  customer={customer}
                />
              )}
            </div>
          </div>
        )}
      </div>
    </CompanyAdminLayout>
  );
};

export default CustomerDetailPage;
